import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from './supabase';

// ── Theme ─────────────────────────────────────────────────────────────────────
// Primary accent: teal. Secondary: purple. No orange/yellow anywhere.
const T = {
  accent:  '#0eb8a0',   // teal
  accent2: '#9b6dff',   // purple
  accentBg:'rgba(14,184,160,.12)',
  accent2Bg:'rgba(155,109,255,.12)',
  accentGlow:'rgba(14,184,160,.35)',
  danger:  '#e04040',
  ok:      '#39d98a',
  info:    '#4a9eff',
  warn:    '#b06af0',
};

const CATS = {
  Guitars:   { label:'Guitars',            color:'#0eb8a0' },
  Bass:      { label:'Bass',               color:'#00c9a7' },
  Amps:      { label:'Amps',               color:'#9b6dff' },
  Pedals:    { label:'Pedals & Effects',   color:'#4a9eff' },
  LiveSound: { label:'Live Sound',         color:'#39d98a' },
  Drums:     { label:'Drums & Perc.',      color:'#b06af0' },
  Keys:      { label:'Keyboards & Synths', color:'#f0a030' },
  Studio:    { label:'Studio Gear',        color:'#e0607e' },
  Orchestra: { label:'Orchestra',           color:'#d4a017' },
};
const CONDITIONS   = ['Mint','Excellent','Very Good','Good','Fair'];
const TUNING_GROUPS = [
  { group: 'Standard', options: [
    'Standard (E)',
    'Eb / D# Standard',
    'D Standard',
    'C# / Db Standard',
    'C Standard',
    'B Standard',
    'Bb / A# Standard',
    'A Standard',
    'Ab / G# Standard',
    'G Standard',
    'F# / Gb Standard',
    'F Standard',
  ]},
  { group: 'Drop', options: [
    'Drop D',
    'Drop C# / Db',
    'Drop C',
    'Drop B',
    'Drop Bb / A#',
    'Drop A',
    'Drop Ab / G#',
    'Drop G',
    'Drop F# / Gb',
    'Drop F',
    'Drop E (one step down)',
  ]},
  { group: 'Double Drop', options: [
    'Double Drop D (DADGBD)',
    'Double Drop C',
    'Double Drop B',
    'Double Drop A',
  ]},
  { group: 'Open', options: [
    'Open E',
    'Open D',
    'Open G',
    'Open A',
    'Open C',
    'Open B',
  ]},
  { group: 'Misc / Modal', options: [
    'DADGAD',
    'DGDGBD (Open G alt)',
    'CGDGAD',
    'NST (New Standard — CGDAEG)',
  ]},
  { group: 'Other', options: ['Other / Custom'] },
];
// Grouped gauge presets — { group, options: [{value, label}] }
const STRING_GAUGE_GROUPS = [
  { group: 'Ernie Ball — Electric', options: [
    { value: 'EB Zippy Slinky (.007-.036)',           label: 'Zippy Slinky (.007-.036)' },
    { value: 'EB Extra Slinky (.008-.038)',            label: 'Extra Slinky (.008-.038)' },
    { value: 'EB Hyper Slinky (.008-.042)',            label: 'Hyper Slinky (.008-.042)' },
    { value: 'EB Mighty Slinky (.0085-.040)',          label: 'Mighty Slinky (.0085-.040)' },
    { value: 'EB Super Slinky (.009-.042)',            label: 'Super Slinky (.009-.042)' },
    { value: 'EB Hybrid Slinky (.009-.046)',           label: 'Hybrid Slinky (.009-.046)' },
    { value: 'EB Primo Slinky (.0095-.044)',           label: 'Primo Slinky (.0095-.044)' },
    { value: 'EB Regular Slinky (.010-.046)',          label: 'Regular Slinky (.010-.046)' },
    { value: 'EB Turbo Slinky (.0105-.048)',           label: 'Turbo Slinky (.0105-.048)' },
    { value: 'EB Mega Slinky (.0105-.048)',            label: 'Mega Slinky (.0105-.048)' },
    { value: 'EB Power Slinky (.011-.048)',            label: 'Power Slinky (.011-.048)' },
    { value: 'EB Skinny Top Heavy Bottom (.010-.052)', label: 'Skinny Top Heavy Bottom (.010-.052)' },
    { value: 'EB Skinny Top Beefy Bottom (.010-.054)', label: 'Skinny Top Beefy Bottom (.010-.054)' },
    { value: 'EB Beefy Slinky (.011-.054)',            label: 'Beefy Slinky (.011-.054)' },
    { value: 'EB Not Even Slinky (.012-.056)',         label: 'Not Even Slinky (.012-.056)' },
    { value: 'EB Magnum Slinky (.012-.056 wound G)',   label: 'Magnum Slinky (.012-.056 wound G)' },
  ]},
  { group: 'Ernie Ball — Bass', options: [
    { value: 'EB Extra Slinky Bass (.040-.095)',   label: 'Extra Slinky Bass (.040-.095)' },
    { value: 'EB Hyper Slinky Bass (.040-.100)',   label: 'Hyper Slinky Bass (.040-.100)' },
    { value: 'EB Super Slinky Bass (.045-.100)',   label: 'Super Slinky Bass (.045-.100)' },
    { value: 'EB Regular Slinky Bass (.050-.105)', label: 'Regular Slinky Bass (.050-.105)' },
    { value: 'EB Hybrid Slinky Bass (.045-.105)',  label: 'Hybrid Slinky Bass (.045-.105)' },
    { value: 'EB Power Slinky Bass (.055-.110)',   label: 'Power Slinky Bass (.055-.110)' },
    { value: 'EB Beefy Slinky Bass (.065-.130)',   label: 'Beefy Slinky Bass (.065-.130)' },
  ]},
  { group: "D'Addario — Electric", options: [
    { value: "D'A EXL120 Super Light (.009-.042)",         label: "EXL120 Super Light (.009-.042)" },
    { value: "D'A EXL120+ Super Light Plus (.0095-.044)",  label: "EXL120+ Super Light Plus (.0095-.044)" },
    { value: "D'A EXL110 Regular Light (.010-.046)",       label: "EXL110 Regular Light (.010-.046)" },
    { value: "D'A EXL110+ Regular Light Plus (.0105-.048)",label: "EXL110+ Regular Light Plus (.0105-.048)" },
    { value: "D'A EXL116 Med Top/Heavy Bot (.011-.052)",   label: "EXL116 Med Top/Heavy Bot (.011-.052)" },
    { value: "D'A EXL115 Medium (.011-.049)",              label: "EXL115 Medium (.011-.049)" },
    { value: "D'A EXL140 Light Top/Heavy Bot (.010-.052)", label: "EXL140 Light Top/Heavy Bot (.010-.052)" },
    { value: "D'A EXL148 Extra Heavy (.012-.060)",         label: "EXL148 Extra Heavy (.012-.060)" },
    { value: "D'A NYXL0942 Super Light (.009-.042)",       label: "NYXL0942 Super Light (.009-.042)" },
    { value: "D'A NYXL1046 Regular Light (.010-.046)",     label: "NYXL1046 Regular Light (.010-.046)" },
    { value: "D'A NYXL1149 Medium (.011-.049)",            label: "NYXL1149 Medium (.011-.049)" },
    { value: "D'A NYXL1059 7-String (.010-.059)",          label: "NYXL1059 7-String (.010-.059)" },
  ]},
  { group: "D'Addario — Bass", options: [
    { value: "D'A EXL170 Regular Light (.045-.100)",  label: "EXL170 Regular Light (.045-.100)" },
    { value: "D'A EXL160 Medium (.050-.105)",         label: "EXL160 Medium (.050-.105)" },
    { value: "D'A EXL165 Custom Light (.045-.105)",   label: "EXL165 Custom Light (.045-.105)" },
    { value: "D'A EXL220 Super Light (.040-.095)",    label: "EXL220 Super Light (.040-.095)" },
    { value: "D'A NYXL45100 Regular Light (.045-.100)",label: "NYXL45100 Regular Light (.045-.100)" },
  ]},
  { group: 'Custom', options: [
    { value: '__custom__', label: 'Custom / Enter manually…' },
  ]},
];
const PEDAL_TYPES = [
  { group: 'Drive',       options: ['Overdrive', 'Distortion', 'Fuzz', 'Boost', 'Preamp / Amp-in-a-box'] },
  { group: 'Dynamics',    options: ['Compressor', 'Limiter', 'Noise Gate', 'Volume / Expression'] },
  { group: 'Modulation',  options: ['Chorus', 'Flanger', 'Phaser', 'Tremolo', 'Vibrato', 'Uni-Vibe', 'Ring Modulator'] },
  { group: 'Time-Based',  options: ['Reverb', 'Delay', 'Echo'] },
  { group: 'Pitch',       options: ['Octave', 'Pitch Shifter / Harmonizer', 'Whammy / Dive Bomb', 'Detune / Doubler'] },
  { group: 'Filter',      options: ['Wah', 'Auto-Wah / Envelope Filter', 'EQ / Tone', 'Talk Box'] },
  { group: 'Utility',     options: ['Tuner', 'Buffer', 'Switcher / Router', 'Looper', 'MIDI Controller'] },
  { group: 'Synth / Misc',options: ['Synth / Oscillator', 'Bit Crusher / Lo-Fi', 'Acoustic Simulator', 'Multi-Effects', 'Other'] },
];
const LIVE_TYPES = [
  { group: 'Microphones',      options: ['Dynamic Mic', 'Condenser Mic', 'Ribbon Mic', 'Wireless Handheld', 'Wireless Lavalier / Clip-on', 'Wireless Headset', 'Boundary / PZM', 'DI Box'] },
  { group: 'Monitors',         options: ['Wedge Monitor', 'In-Ear Monitor (IEM)', 'IEM Transmitter / Bodypack', 'Personal Monitor Mixer'] },
  { group: 'Wireless Systems', options: ['Guitar / Instrument Wireless', 'Handheld Wireless System', 'Bodypack Transmitter', 'Wireless Receiver'] },
  { group: 'Mixers & Signal',  options: ['Analog Mixer', 'Digital Mixer', 'Stage Box / Snake', 'Patchbay', 'Signal Processor / EQ', 'Crossover'] },
  { group: 'Amplification',    options: ['Power Amplifier', 'Powered Speaker', 'Subwoofer', 'Column Array', 'Monitor Amplifier'] },
  { group: 'Lighting',         options: ['Par Can / LED Par', 'Moving Head', 'Spotlight', 'Strobe', 'Haze / Fog Machine', 'Lighting Controller / DMX'] },
  { group: 'Cables & Stands',  options: ['XLR Cable', 'Speaker Cable', 'Mic Stand', 'Boom Arm', 'Cable Snake', 'Rack Case'] },
  { group: 'Misc / Other',     options: ['Headphone Amp', 'Talkback System', 'Recording Interface', 'Playback Device', 'Other'] },
];

const ORCHESTRA_SECTIONS = [
  { section: 'Strings',     instruments: ['Violin','Viola','Cello','Double Bass','Harp','Lute','Mandolin','Banjo','Ukulele','Sitar','Erhu'] },
  { section: 'Woodwinds',   instruments: ['Flute','Piccolo','Oboe','Cor Anglais','Clarinet','Bass Clarinet','Bassoon','Contrabassoon','Soprano Sax','Alto Sax','Tenor Sax','Baritone Sax'] },
  { section: 'Brass',       instruments: ['Trumpet','Cornet','Flugelhorn','French Horn','Trombone','Bass Trombone','Euphonium','Tuba','Sousaphone','Bugle'] },
  { section: 'Percussion',  instruments: ['Snare Drum','Bass Drum','Timpani','Xylophone','Marimba','Vibraphone','Glockenspiel','Tubular Bells','Cymbals','Triangle','Tambourine','Castanets','Congas','Bongos','Djembe','Cajon','Steel Pan'] },
];

const DONATE_URL = 'https://buymeacoffee.com/gearvault'; // ← update with your BMC URL

const EMPTY_FORM   = { cat:'Guitars', cond:'Excellent', make:'', model:'', yr:'', sn:'', paid:'', mv:'', notes:'', imgs:[], tuning:'', gauge:'', pedal_type:'', live_type:'', orch_section:'', orch_instrument:'', reverb_query:'', tags:[] };
const TAGS_CACHE_KEY  = 'gearvault-user-tags';
const PREFS_CACHE_KEY    = 'gearvault-view-prefs';
const ONBOARDED_KEY      = 'gearvault-onboarded';

// ── Serial Date Decoder ────────────────────────────────────────────────────────
// Returns { year, detail, confidence } or null
function decodeSerial(make, sn) {
  if (!sn || !make) return null;
  const s = sn.trim().toUpperCase().replace(/\s+/g,'');
  const m = make.trim().toLowerCase();

  // ── MARTIN ────────────────────────────────────────────────────────────────
  if (m.includes('martin') || m === 'cf martin' || m === 'c.f. martin') {
    const n = parseInt(s, 10);
    if (!isNaN(n) && n > 0) {
      const MARTIN = [
        [8349,1898],[8716,1899],[9128,1900],[9310,1901],[9528,1902],[9810,1903],[9988,1904],
        [10120,1905],[10329,1906],[10727,1907],[10883,1908],[11018,1909],[11203,1910],
        [11413,1911],[11565,1912],[11821,1913],[12047,1914],[12209,1915],[12390,1916],
        [12988,1917],[13450,1918],[14512,1919],[15848,1920],[16758,1921],[17839,1922],
        [19891,1923],[22008,1924],[24116,1925],[28689,1926],[34435,1927],[37568,1928],
        [40843,1929],[45317,1930],[49589,1931],[52590,1932],[55084,1933],[58679,1934],
        [61947,1935],[65176,1936],[68865,1937],[71866,1938],[74061,1939],[76734,1940],
        [80013,1941],[83107,1942],[86724,1943],[90149,1944],[93623,1945],[98158,1946],
        [103468,1947],[108269,1948],[111821,1949],[117961,1950],[122799,1951],[128436,1952],
        [134501,1953],[141345,1954],[147328,1955],[152775,1956],[159061,1957],[165576,1958],
        [171047,1959],[175689,1960],[181297,1961],[187384,1962],[193327,1963],[199626,1964],
        [207030,1965],[217215,1966],[230095,1967],[241925,1968],[256003,1969],[271633,1970],
        [294270,1971],[313302,1972],[333873,1973],[353387,1974],[371828,1975],[388800,1976],
        [399625,1977],[407800,1978],[417711,1979],[433140,1980],[469843,1981],[902908,1982],
        [522306,1983],[535223,1984],[548234,1985],[564376,1986],[578369,1987],[597979,1988],
        [617367,1989],[637389,1990],[657970,1991],[679746,1992],[699891,1993],[728936,1994],
        [750000,1995],[776034,1996],[798183,1997],[820686,1998],[845644,1999],[900000,2000],
        // Note: 900001–902908 were Sigma-Martin serials 1981-82
        [916759,2001],[941050,2002],[966000,2003],[1008000,2004],[1050000,2005],
        [1090000,2006],[1130000,2007],[1175000,2008],[1220000,2009],[1270000,2010],
        [1320000,2011],[1370000,2012],[1430000,2013],[1490000,2014],[1550000,2015],
        [1615000,2016],[1680000,2017],[1750000,2018],[1820000,2019],[1900000,2020],
        [1980000,2021],[2060000,2022],[2145000,2023],[2230000,2024],
      ];
      for (let i = 0; i < MARTIN.length; i++) {
        if (n <= MARTIN[i][0]) {
          const yr = MARTIN[i][1];
          return { year: yr, detail: `Serial #${n} — Martin sequential #${n}`, confidence: 'high' };
        }
      }
      return { year: '2024+', detail: `Serial #${n} — recent production`, confidence: 'medium' };
    }
    return null;
  }

  // ── TAYLOR ────────────────────────────────────────────────────────────────
  if (m.includes('taylor')) {
    // 10-digit (Nov 2009+): F Y MM DD Y seq  — positions 0=factory, 1=yr1, 2-3=mm, 4-5=dd, 6=yr2, 7-9=seq
    if (s.length === 10 && /^\d{10}$/.test(s)) {
      const factory = s[0]==='1' ? 'El Cajon, CA (USA)' : s[0]==='2' ? 'Tecate, Mexico' : 'Unknown factory';
      const yr1 = s[1]; const yr2 = s[6];
      const year = parseInt('20'+yr1+yr2,10); // decode split year
      const month = parseInt(s.slice(2,4),10);
      const day   = parseInt(s.slice(4,6),10);
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const seq = parseInt(s.slice(7),10);
      const dateStr = `${MONTHS[month-1]||'?'} ${day}, ${year}`;
      return { year, detail: `${factory} — started ${dateStr}, guitar #${seq} that day`, confidence: 'high' };
    }
    // 11-digit (Jan 2000–Oct 2009): YYYY MM DD S SS
    if (s.length === 11 && /^\d{11}$/.test(s)) {
      const year = parseInt(s.slice(0,4),10);
      const month = parseInt(s.slice(4,6),10);
      const day   = parseInt(s.slice(6,8),10);
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return { year, detail: `Started ${MONTHS[month-1]||'?'} ${day}, ${year}`, confidence: 'high' };
    }
    // 9-digit (1993–1999): YY MM DD S SS
    if (s.length === 9 && /^\d{9}$/.test(s)) {
      const yy = parseInt(s.slice(0,2),10);
      const year = yy >= 93 ? 1900+yy : 2000+yy;
      const month = parseInt(s.slice(2,4),10);
      const day   = parseInt(s.slice(4,6),10);
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return { year, detail: `Started ${MONTHS[month-1]||'?'} ${day}, ${year}`, confidence: 'high' };
    }
    // pre-1993 sequential
    const TAYLOR_EARLY = [
      [340,1975],[348,1976],[534,1977],[899,1978],[1395,1979],[2764,1980],
      [4766,1981],[6823,1982],[9209,1983],[11025,1984],[13029,1985],[16027,1986],
      [19028,1987],[25027,1988],[32027,1989],[40027,1990],[49027,1991],[62027,1992],
    ];
    const n = parseInt(s, 10);
    if (!isNaN(n)) {
      for (let i = 0; i < TAYLOR_EARLY.length; i++) {
        if (n <= TAYLOR_EARLY[i][0]) return { year: TAYLOR_EARLY[i][1], detail: `Early Taylor serial #${n}`, confidence: 'medium' };
      }
    }
    return null;
  }

  // ── GIBSON ────────────────────────────────────────────────────────────────
  if (m.includes('gibson')) {
    // 9-digit (July 2005+): YDDDYBXXX
    if (s.length === 9 && /^\d{9}$/.test(s)) {
      const yr1 = parseInt(s[0],10); const yr2 = parseInt(s[4],10);
      const ddd = parseInt(s.slice(1,4),10);
      const decade = yr1 >= 0 && yr1 <= 9 ? (yr1 < 3 ? 2000 : 2000) : 2000;
      // reconstruct year: first digit + 5th digit = YY
      const yyStr = s[0]+s[4];
      let year;
      const yyNum = parseInt(yyStr,10);
      year = 2000 + yyNum; // 05 = 2005, 14 = 2014, etc
      if (year > 2030) year = 1900 + yyNum;
      const date = new Date(year, 0); date.setDate(ddd);
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return { year, detail: `Day ${ddd} of ${year} (≈ ${MONTHS[date.getMonth()]} ${date.getDate()})`, confidence: 'high' };
    }
    // 8-digit (1977–2005): YDDDYXXX
    if (s.length === 8 && /^\d{8}$/.test(s)) {
      const yyStr = s[0]+s[4];
      const yyNum = parseInt(yyStr,10);
      let year = 1900 + yyNum;
      if (year < 1970) year = 2000 + yyNum;
      if (year > 2005) year = 1900 + yyNum; // pre-2005 format
      const ddd = parseInt(s.slice(1,4),10);
      const ranking = parseInt(s.slice(5),10);
      const factory = year < 1985 ? (ranking < 500 ? 'Kalamazoo, MI' : 'Nashville, TN') :
                      year < 1990 ? 'Nashville, TN' :
                      ranking < 300 ? 'Bozeman, MT (acoustic)' : 'Nashville, TN (electric)';
      const date = new Date(year, 0); date.setDate(ddd);
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return { year, detail: `Day ${ddd} of ${year} (≈ ${MONTHS[date.getMonth()]} ${date.getDate()}) — ${factory}`, confidence: 'high' };
    }
    // 1975-1977 decal: 8-digit with YY prefix
    if (s.length === 8 && /^\d{8}$/.test(s)) {
      const yy = parseInt(s.slice(0,2),10);
      if (yy >= 99 || yy <= 6) {
        const year = yy >= 99 ? 1975 : yy === 0 ? 1976 : yy <= 6 ? 1977 : null;
        if (year) return { year, detail: `Transitional decal serial (${year})`, confidence: 'medium' };
      }
    }
    return null;
  }

  // ── FENDER ────────────────────────────────────────────────────────────────
  if (m.includes('fender') || m.includes('squier')) {
    // US prefix (2010+): US + YY + seq
    if (/^US\d/.test(s)) {
      const yy = parseInt(s.slice(2,4),10);
      const year = 2000+yy;
      return { year, detail: `USA production, ${year}`, confidence: 'high' };
    }
    // MX prefix (Mexico 2010+): MX + YY + seq
    if (/^MX\d/.test(s)) {
      const yy = parseInt(s.slice(2,4),10);
      return { year: 2000+yy, detail: `Mexico (Ensenada), ${2000+yy}`, confidence: 'high' };
    }
    // Z/DZ (2000s USA): Z0–Z9, DZ0–DZ9
    const mZ = s.match(/^(DZ|Z)(\d)/);
    if (mZ) {
      const year = 2000 + parseInt(mZ[2],10);
      const series = mZ[1]==='DZ' ? 'American Deluxe' : 'American Standard';
      return { year, detail: `${series}, ${year}`, confidence: 'high' };
    }
    // MZ (Mexico 2000s): MZ + digit
    if (/^MZ(\d)/.test(s)) {
      const year = 2000 + parseInt(s[2],10);
      return { year, detail: `Mexico (Ensenada), ${year}`, confidence: 'high' };
    }
    // MN (Mexico 1990s): MN + digit
    if (/^MN(\d)/.test(s)) {
      const year = 1990 + parseInt(s[2],10);
      return { year, detail: `Mexico (Ensenada), ${year}`, confidence: 'high' };
    }
    // N (USA 1990s): N + digit
    if (/^N(\d)/.test(s) && !/^MN/.test(s)) {
      const year = 1990 + parseInt(s[1],10);
      return { year, detail: `USA, ${year}`, confidence: 'high' };
    }
    // E (USA 1980s): E + digit
    if (/^E(\d)/.test(s) && !/^[A-Z]{2}E/.test(s)) {
      const digit = parseInt(s[1],10);
      const year = 1980 + digit;
      return { year, detail: `USA, ${year}`, confidence: digit <= 9 ? 'high' : 'medium' };
    }
    // S (USA 1970s): S + digit
    if (/^S(\d)/.test(s) && !/^[A-Z]{2}/.test(s)) {
      const year = 1970 + parseInt(s[1],10);
      return { year, detail: `USA, ${year}`, confidence: 'medium' };
    }
    // Japan MIJ/CIJ: JV, SQ, JD, A-V + 6 digits
    if (/^(JV|SQ)\d/.test(s)) {
      return { year: '1982–1984', detail: `Made in Japan (FujiGen) — early MIJ`, confidence: 'medium' };
    }
    if (/^JD\d{8}/.test(s)) {
      const yy = parseInt(s.slice(2,4),10);
      return { year: 2012+yy, detail: `Made in Japan, ${2012+yy}`, confidence: 'high' };
    }
    return null;
  }

  // ── PRS ───────────────────────────────────────────────────────────────────
  if (m.includes('prs') || m.includes('paul reed smith')) {
    // Two-digit year prefix (2008+): 08XXXXXX+
    const twoDigit = s.match(/^(\d{2})(\d+)$/);
    if (twoDigit) {
      const yy = parseInt(twoDigit[1],10);
      if (yy >= 8 && yy <= 30) {
        const year = 2000+yy;
        const seq = parseInt(twoDigit[2],10);
        return { year, detail: `USA, ${year} — sequential #${seq}`, confidence: 'high' };
      }
    }
    // SE Korean letter prefix (A=2000, B=2001,…)
    const seLetter = s.match(/^([A-Z])(\d+)$/);
    if (seLetter) {
      const letterYear = seLetter[1].charCodeAt(0) - 'A'.charCodeAt(0) + 2000;
      if (letterYear >= 2000 && letterYear <= 2030) {
        return { year: letterYear, detail: `SE Series (Korea/Indonesia), ${letterYear}`, confidence: 'high' };
      }
    }
    // S2 prefix
    if (/^S2/.test(s)) {
      const yy = parseInt(s.slice(2,4),10);
      if (!isNaN(yy)) return { year: 2000+yy, detail: `S2 Series (USA), ${2000+yy}`, confidence: 'high' };
    }
    // Single-digit prefix (ambiguous decade): match sequential range
    const singleDigit = s.match(/^(\d)(\d+)$/);
    if (singleDigit) {
      const digit = parseInt(singleDigit[1],10);
      const seq = parseInt(singleDigit[2],10);
      // PRS sequential ranges by year (approximate)
      const PRS_RANGES = [
        [400,1985],[1700,1986],[3500,1987],[5400,1988],[7600,1989],[10100,1990],
        [12600,1991],[15000,1992],[17900,1993],[20900,1994],[24600,1995],[29500,1996],
        [34600,1997],[39100,1998],[44499,1999],[52199,2000],[62199,2001],[72353,2002],
        [82254,2003],[92555,2004],[103103,2005],[114940,2006],[132401,2007],
      ];
      for (let i = 0; i < PRS_RANGES.length; i++) {
        if (seq <= PRS_RANGES[i][0]) {
          return { year: PRS_RANGES[i][1], detail: `USA, ${PRS_RANGES[i][1]} — sequential #${seq}`, confidence: 'medium' };
        }
      }
    }
    return null;
  }

  // ── IBANEZ ────────────────────────────────────────────────────────────────
  if (m.includes('ibanez')) {
    // FujiGen modern (1997+): F + 2-digit year + 5 digits → F0812345 = 2008
    const fMod = s.match(/^F(\d{2})(\d{5})$/);
    if (fMod) {
      const yy = parseInt(fMod[1],10);
      const year = yy < 80 ? 2000+yy : 1900+yy;
      const seq = parseInt(fMod[2],10);
      const MONTH_CODES = ['Nov','Dec (1-2999)','Jan (3000-5999)','Feb (6000-8999)','Mar (9000-11999)','Apr (12000-14999)','May (15000-17999)','Jun (18000-20999)','Jul (21000-23999)','Aug (24000-26999)','Sep (27000-29999)','Oct (30000-32999)'];
      return { year, detail: `FujiGen, Japan, ${year} — #${seq}`, confidence: 'high' };
    }
    // FujiGen vintage (1978-1986): letter + 2-digit year + 4 digits → B801234 = Feb 1980
    const fVint = s.match(/^([A-L])(\d{2})(\d{4})$/);
    if (fVint) {
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const month = MONTHS[fVint[1].charCodeAt(0) - 'A'.charCodeAt(0)] || '?';
      const yy = parseInt(fVint[2],10);
      const year = yy >= 75 ? 1900+yy : 2000+yy;
      return { year, detail: `Japan (FujiGen/Terada), ${month} ${year}`, confidence: 'high' };
    }
    // FujiGen multi-factory (1986-1996): F/I/H + digit(year) + 5 digits
    const fFactory = s.match(/^([FIH])(\d)(\d{5})$/);
    if (fFactory) {
      const factories = { F:'FujiGen', I:'Iida Gakki', H:'Terada' };
      const factory = factories[fFactory[1]] || 'Japan';
      const digit = parseInt(fFactory[2],10);
      const year = `199${digit}` ; // rough
      return { year: parseInt(year,10), detail: `${factory}, Japan, ~${year}`, confidence: 'medium' };
    }
    // Korean (1987-2008): C/A/Y/P/E + digit(year) + seq
    const kMod = s.match(/^([CAYP])(\d)(\d+)$/);
    if (kMod) {
      const factoryK = { C:'Cort, Korea', A:'Saein, Korea', Y:'Yoojin, Korea', P:'Peerless, Korea' };
      const factory = factoryK[kMod[1]] || 'Korea';
      const digit = parseInt(kMod[2],10);
      const year = digit >= 7 ? 1980+digit : 1990+digit;
      return { year, detail: `${factory}, ~${year}`, confidence: 'medium' };
    }
    // Indonesian modern: I/K/J + 2-digit year + 2-digit month + seq
    const inMod = s.match(/^([IKJ])(\d{2})(\d{2})(\d+)$/);
    if (inMod) {
      const factoryI = { I:'Cort Indonesia', K:'Kwo Hsiao', J:'Sejung' };
      const factory = factoryI[inMod[1]] || 'Indonesia';
      const yy = parseInt(inMod[2],10);
      const year = 2000+yy;
      const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const month = MONTHS[parseInt(inMod[3],10)-1] || '?';
      return { year, detail: `${factory}, ${month} ${year}`, confidence: 'high' };
    }
    return null;
  }

  // ── CHARVEL ───────────────────────────────────────────────────────────────
  if (m.includes('charvel')) {
    // Modern (2013+): alphanumeric 10-digit, 2 numeric digits after letters = year
    // e.g. ICJ1500001 → year 15 = 2015
    const modMatch = s.match(/^[A-Z]{2,3}(\d{2})\d+$/);
    if (modMatch) {
      const year = 2000 + parseInt(modMatch[1],10);
      return { year, detail: `Charvel import (Pro-Mod/DK24), ${year}`, confidence: 'high' };
    }
    // USA San Dimas (1981-1986): numeric neck plate #1001–#5491
    const sanDimas = parseInt(s, 10);
    if (!isNaN(sanDimas) && sanDimas >= 1001 && sanDimas <= 5491) {
      let year;
      if (sanDimas < 1096) year = 1981;
      else if (sanDimas < 1725) year = 1982;
      else if (sanDimas < 2939) year = 1983;
      else if (sanDimas < 4262) year = 1984;
      else if (sanDimas < 5304) year = 1985;
      else year = 1986;
      return { year, detail: `USA San Dimas, ${year} — neck plate #${sanDimas}`, confidence: 'high' };
    }
    // Japanese (6-digit, 220000+): Made in Japan Chu Shin Gakki
    if (/^\d{6}$/.test(s)) {
      const n = parseInt(s,10);
      if (n >= 220000) {
        return { year: '1986–1991', detail: `Japan (Chu Shin Gakki) — 6-digit import serial`, confidence: 'medium' };
      }
    }
    return null;
  }

  return null;
}


const MAX_IMGS     = 2;
const VAULT_CACHE_KEY = 'gearvault-gear-cache';

function uid()      { return Date.now().toString(36)+Math.random().toString(36).slice(2,6); }
function fmt(n)     { return isNaN(n)?'0':Number(n).toLocaleString('en-US',{minimumFractionDigits:0,maximumFractionDigits:0}); }
function fmtMoney(n){ return isNaN(n)?'—':'$'+fmt(n); }

// Universal share icon (iOS/Android style — box with arrow up)
function ShareIcon({ size=20, color='currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 12H3v9h18v-9h-5"/>
      <polyline points="16 6 12 2 8 6"/>
      <line x1="12" y1="2" x2="12" y2="15"/>
    </svg>
  );
}

// ── Share Image Generator ─────────────────────────────────────────────────────
// Draws a branded gear card on an offscreen canvas and returns a PNG File
async function generateShareImage(item) {
  const W = 1080, H = 1920;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  const cat      = CATS[item.cat] || { label: item.cat, color: '#7a7872' };
  const catColor = cat.color;
  const val      = item.rv || item.mv;
  const imgs     = item.imgs || (item.img ? [item.img] : []);

  // ── Full background ──────────────────────────────────────────────────────
  ctx.fillStyle = '#111112';
  ctx.fillRect(0, 0, W, H);

  // ── Photo zone: top 60% of canvas ───────────────────────────────────────
  const PHOTO_H = Math.round(H * 0.60); // 1152px

  if (imgs.length > 0) {
    await new Promise(resolve => {
      const img = new Image();
      img.onload = () => {
        const aspect = img.naturalWidth / img.naturalHeight;
        const targetAspect = W / PHOTO_H;
        let sx = 0, sy = 0, sw = img.naturalWidth, sh = img.naturalHeight;
        if (aspect > targetAspect) {
          sw = img.naturalHeight * targetAspect;
          sx = (img.naturalWidth - sw) / 2;
        } else {
          sh = img.naturalWidth / targetAspect;
          sy = (img.naturalHeight - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, W, PHOTO_H);
        resolve();
      };
      img.onerror = resolve;
      img.src = imgs[0];
    });
  } else {
    // No photo — textured placeholder
    const noPhotoGrad = ctx.createLinearGradient(0, 0, W, PHOTO_H);
    noPhotoGrad.addColorStop(0, '#1a1a1e');
    noPhotoGrad.addColorStop(1, '#0d0d0f');
    ctx.fillStyle = noPhotoGrad;
    ctx.fillRect(0, 0, W, PHOTO_H);
    // Grid texture
    ctx.strokeStyle = 'rgba(255,255,255,0.03)';
    ctx.lineWidth = 1;
    for (let x = 0; x < W; x += 80) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,PHOTO_H); ctx.stroke(); }
    for (let y = 0; y < PHOTO_H; y += 80) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke(); }
    // Category watermark
    ctx.save();
    ctx.font = `bold ${W * 0.18}px sans-serif`;
    ctx.fillStyle = catColor + '12';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(cat.label.toUpperCase(), W / 2, PHOTO_H / 2);
    ctx.restore();
  }

  // Gradient fade: photo bleeds into info panel
  const fadeGrad = ctx.createLinearGradient(0, PHOTO_H - 280, 0, PHOTO_H + 60);
  fadeGrad.addColorStop(0, 'transparent');
  fadeGrad.addColorStop(0.6, '#111112cc');
  fadeGrad.addColorStop(1,   '#111112');
  ctx.fillStyle = fadeGrad;
  ctx.fillRect(0, PHOTO_H - 280, W, 340);

  // ── Top accent bar (sits over photo) ────────────────────────────────────
  ctx.fillStyle = catColor;
  ctx.fillRect(0, 0, W, 10);
  const topGlow = ctx.createLinearGradient(0, 10, 0, 90);
  topGlow.addColorStop(0, catColor + '55');
  topGlow.addColorStop(1, 'transparent');
  ctx.fillStyle = topGlow;
  ctx.fillRect(0, 10, W, 80);

  // ── Info panel: bottom 40% ───────────────────────────────────────────────
  const INFO_TOP = PHOTO_H;           // 1152
  const PAD = 72;

  // Subtle grid in info area
  ctx.strokeStyle = 'rgba(255,255,255,0.018)';
  ctx.lineWidth = 1;
  for (let x = 0; x < W; x += 80) { ctx.beginPath(); ctx.moveTo(x, INFO_TOP); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = INFO_TOP; y < H; y += 80) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  let cursor = INFO_TOP + 56;

  // ── Make ──────────────────────────────────────────────────────────────
  ctx.textAlign = 'left';
  ctx.font = `500 ${58}px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
  ctx.fillStyle = '#7a7a78';
  ctx.fillText(item.make.toUpperCase(), PAD, cursor);
  cursor += 72;

  // ── Model + Year ──────────────────────────────────────────────────────
  ctx.font = `bold ${100}px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
  ctx.fillStyle = '#f0ede6';
  const modelLine = item.model + (item.yr ? '  ' + item.yr : '');
  let modelText = modelLine;
  while (ctx.measureText(modelText).width > W - PAD * 2 && modelText.length > 4)
    modelText = modelText.slice(0, -1);
  if (modelText !== modelLine) modelText = modelText.trimEnd() + '…';
  ctx.fillText(modelText, PAD, cursor);
  cursor += 54;

  // ── Pills row ─────────────────────────────────────────────────────────
  cursor += 20;
  const PILL_H = 52, PILL_R = 26;
  ctx.font = `600 32px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
  let pillX = PAD;

  const drawPill = (label, bgColor, borderColor, textColor) => {
    const tw = ctx.measureText(label).width;
    const pw = tw + 48;
    ctx.fillStyle = bgColor;
    roundRect(ctx, pillX, cursor, pw, PILL_H, PILL_R); ctx.fill();
    ctx.strokeStyle = borderColor; ctx.lineWidth = 1.5;
    roundRect(ctx, pillX, cursor, pw, PILL_H, PILL_R); ctx.stroke();
    ctx.fillStyle = textColor;
    ctx.fillText(label, pillX + 24, cursor + 34);
    pillX += pw + 16;
  };

  drawPill(cat.label.toUpperCase(), catColor + '22', catColor + '77', catColor);
  if (item.cond) drawPill(item.cond.toUpperCase(), '#2e2e3388', '#44444466', '#9b9b99');
  cursor += PILL_H + 52;

  // ── Divider ───────────────────────────────────────────────────────────
  ctx.strokeStyle = '#2e2e33';
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(PAD, cursor); ctx.lineTo(W - PAD, cursor); ctx.stroke();
  cursor += 52;

  // ── Stats grid ────────────────────────────────────────────────────────
  const statCols = [];
  if (item.sn)  statCols.push(['SERIAL #', item.sn]);
  if (val)      statCols.push(['EST. VALUE', fmtMoney(parseFloat(val))]);
  if (item.tuning && (item.cat==='Guitars'||item.cat==='Bass')) statCols.push(['TUNING', item.tuning]);
  if (item.gauge && (item.cat==='Guitars'||item.cat==='Bass'))
    statCols.push(['GAUGE', item.gauge.replace(/^(EB |D'A )/, '')]);

  if (statCols.length > 0) {
    const cols = Math.min(statCols.length, 3);
    const colW = (W - PAD * 2) / cols;
    statCols.slice(0, cols).forEach(([label, value], i) => {
      const x = PAD + i * colW;
      ctx.font = `500 28px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
      ctx.fillStyle = '#4a4a4a';
      ctx.textAlign = 'left';
      ctx.fillText(label, x, cursor);
      ctx.font = `600 44px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
      ctx.fillStyle = (label === 'EST. VALUE') ? catColor : '#d4d2cc';
      let v = String(value);
      while (ctx.measureText(v).width > colW - 24 && v.length > 2) v = v.slice(0, -1);
      if (v !== String(value)) v = v.trimEnd() + '…';
      ctx.fillText(v, x, cursor + 50);
    });
    cursor += 110;
  }

  // ── Footer branding ───────────────────────────────────────────────────
  const footY = H - 70;
  ctx.strokeStyle = '#2a2a2e';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(PAD, footY - 28); ctx.lineTo(W - PAD, footY - 28); ctx.stroke();

  // Pick mark
  const px = PAD, py = footY - 14;
  ctx.fillStyle = '#0eb8a0';
  ctx.beginPath();
  ctx.moveTo(px + 12, py);
  ctx.quadraticCurveTo(px + 24, py - 2, px + 24, py + 12);
  ctx.quadraticCurveTo(px + 24, py + 26, px + 12, py + 30);
  ctx.quadraticCurveTo(px, py + 26, px, py + 12);
  ctx.quadraticCurveTo(px, py - 2, px + 12, py);
  ctx.fill();

  ctx.font = `bold 34px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
  ctx.fillStyle = '#0eb8a0';
  ctx.textAlign = 'left';
  ctx.fillText('GEARVAULT', PAD + 34, footY + 8);

  ctx.font = `400 30px "Barlow Condensed", Arial Narrow, Arial, sans-serif`;
  ctx.fillStyle = '#3a3a3a';
  ctx.textAlign = 'right';
  ctx.fillText('gearvault.xyz', W - PAD, footY + 8);

  // ── Export ────────────────────────────────────────────────────────────
  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const fname = `${item.make}-${item.model}.png`.replace(/\s+/g, '-');
      resolve(new File([blob], fname, { type: 'image/png' }));
    }, 'image/png');
  });
}

// helper: draw rounded rect path
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x+r, y);
  ctx.lineTo(x+w-r, y); ctx.quadraticCurveTo(x+w, y, x+w, y+r);
  ctx.lineTo(x+w, y+h-r); ctx.quadraticCurveTo(x+w, y+h, x+w-r, y+h);
  ctx.lineTo(x+r, y+h); ctx.quadraticCurveTo(x, y+h, x, y+h-r);
  ctx.lineTo(x, y+r); ctx.quadraticCurveTo(x, y, x+r, y);
  ctx.closePath();
}

function useIsMobile() {
  const [mob, setMob] = useState(()=>window.innerWidth < 640);
  useEffect(()=>{
    const fn=()=>setMob(window.innerWidth<640);
    window.addEventListener('resize',fn);
    return ()=>window.removeEventListener('resize',fn);
  },[]);
  return mob;
}

const inputStyle = {
  background:'#0d0d0e', border:'1px solid #2e2e33', color:'#d4d2cc',
  fontFamily:'inherit', fontSize:'16px', letterSpacing:'.02em',
  padding:'.65rem .85rem', borderRadius:4, outline:'none',
  width:'100%', boxSizing:'border-box',
  boxShadow:'inset 0 1px 3px rgba(0,0,0,.4)', WebkitAppearance:'none',
};

function Btn({ onClick, children, variant='default', size='md', style={}, disabled=false }) {
  const base = {
    fontFamily:'inherit',
    fontSize: size==='xs'?'.62rem':size==='sm'?'.7rem':'.75rem',
    letterSpacing:'.08em', textTransform:'uppercase',
    padding: size==='xs'?'.28rem .5rem':size==='sm'?'.48rem .8rem':'.6rem 1rem',
    borderRadius:4, cursor:disabled?'not-allowed':'pointer',
    border:'1px solid #2e2e33', background:'#1f1f22', color:'#7a7872',
    transition:'all .12s', whiteSpace:'nowrap', opacity:disabled?.55:1,
    minHeight:44, display:'inline-flex', alignItems:'center', justifyContent:'center',
    WebkitTapHighlightColor:'transparent', ...style,
  };
  const variants = {
    primary:{ background:T.accent,  borderColor:T.accent,  color:'#000', fontWeight:600, boxShadow:`0 2px 12px ${T.accentGlow}` },
    purple: { background:T.accent2, borderColor:T.accent2, color:'#fff', fontWeight:600, boxShadow:'0 2px 12px rgba(155,109,255,.4)' },
    danger:  { background:'transparent', borderColor:T.danger, color:T.danger },
    teal:    { background:'transparent', borderColor:T.accent, color:T.accent },
    ghost:   { background:'transparent', border:'1px solid transparent', color:'#7a7872' },
    silver:  { background:'#2e2e33', borderColor:'#4a4a50', color:'#d4d2cc', fontWeight:600 },
  };
  return <button style={{...base,...(variants[variant]||{})}} onClick={disabled?undefined:onClick} disabled={disabled}>{children}</button>;
}

function Field({ label, children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.38rem' }}>
      <label style={{ fontSize:'.6rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.14em' }}>{label}</label>
      {children}
    </div>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────────
function Lightbox({ imgs, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx||0);
  const touchStart = useRef(null);
  useEffect(()=>{
    const fn=e=>{
      if(e.key==='Escape') onClose();
      if(e.key==='ArrowRight') setIdx(i=>Math.min(i+1,imgs.length-1));
      if(e.key==='ArrowLeft')  setIdx(i=>Math.max(i-1,0));
    };
    window.addEventListener('keydown',fn);
    return ()=>window.removeEventListener('keydown',fn);
  },[imgs,onClose]);
  const onTouchStart=e=>{ touchStart.current=e.touches[0].clientX; };
  const onTouchEnd=e=>{
    if(touchStart.current===null) return;
    const dx=e.changedTouches[0].clientX-touchStart.current;
    if(Math.abs(dx)>50){ dx<0?setIdx(i=>Math.min(i+1,imgs.length-1)):setIdx(i=>Math.max(i-1,0)); }
    touchStart.current=null;
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.97)', zIndex:9000, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div style={{ position:'absolute', top:'env(safe-area-inset-top,1rem)', right:'1rem' }}>
        <Btn variant="ghost" onClick={e=>{e.stopPropagation();onClose();}} style={{ fontSize:'1.4rem', minWidth:44, minHeight:44, color:'#d4d2cc' }}>✕</Btn>
      </div>
      <img src={imgs[idx]} alt="" onClick={e=>e.stopPropagation()}
        style={{ maxWidth:'95vw', maxHeight:'80vh', objectFit:'contain', borderRadius:3, userSelect:'none', pointerEvents:'none', boxShadow:'0 8px 60px rgba(0,0,0,.8)' }} />
      {imgs.length>1 && (
        <div style={{ display:'flex', gap:'.75rem', marginTop:'1.25rem' }} onClick={e=>e.stopPropagation()}>
          {imgs.map((src,i)=>(
            <img key={i} src={src} alt="" onClick={()=>setIdx(i)}
              style={{ width:56, height:56, objectFit:'cover', borderRadius:3, cursor:'pointer', border:`2px solid ${i===idx?T.accent:'#2e2e33'}`, opacity:i===idx?1:.5, transition:'all .15s' }} />
          ))}
        </div>
      )}
      {imgs.length>1 && idx>0 && (
        <div style={{ position:'absolute', left:'.5rem', top:'50%', transform:'translateY(-50%)' }}>
          <Btn variant="ghost" onClick={e=>{e.stopPropagation();setIdx(i=>Math.max(i-1,0));}} style={{ fontSize:'2rem', minHeight:60, minWidth:44, color:'#d4d2cc' }}>‹</Btn>
        </div>
      )}
      {imgs.length>1 && idx<imgs.length-1 && (
        <div style={{ position:'absolute', right:'.5rem', top:'50%', transform:'translateY(-50%)' }}>
          <Btn variant="ghost" onClick={e=>{e.stopPropagation();setIdx(i=>Math.min(i+1,imgs.length-1));}} style={{ fontSize:'2rem', minHeight:60, minWidth:44, color:'#d4d2cc' }}>›</Btn>
        </div>
      )}
      {imgs.length>1 && (
        <div style={{ position:'absolute', bottom:'2rem', display:'flex', gap:'.4rem' }}>
          {imgs.map((_,i)=><div key={i} style={{ width:6, height:6, borderRadius:'50%', background:i===idx?T.accent:'#444', transition:'background .2s' }} />)}
        </div>
      )}
    </div>
  );
}

// ── Image Uploader ────────────────────────────────────────────────────────────
function ImageUploader({ imgs, onChange }) {
  const fileRef = useRef();
  function handleFiles(e) {
    const files=Array.from(e.target.files), remaining=MAX_IMGS-imgs.length;
    if(remaining<=0) return;
    files.slice(0,remaining).forEach(file=>{
      if(file.size>5*1024*1024){ alert(`${file.name} too large (max 5MB)`); return; }
      const r=new FileReader();
      r.onload=ev=>onChange(prev=>[...prev,ev.target.result].slice(0,MAX_IMGS));
      r.readAsDataURL(file);
    });
    e.target.value='';
  }
  function removeImg(i){ onChange(prev=>prev.filter((_,idx)=>idx!==i)); }
  const canAdd=imgs.length<MAX_IMGS;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.7rem' }}>
      {imgs.length>0 && (
        <div style={{ display:'flex', gap:'.7rem' }}>
          {imgs.map((src,i)=>(
            <div key={i} style={{ position:'relative', width:96, height:96, borderRadius:4, overflow:'hidden', border:'1px solid #2e2e33', flexShrink:0 }}>
              <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
              <button onClick={()=>removeImg(i)} style={{ position:'absolute', top:3, right:3, background:'rgba(0,0,0,.8)', border:'none', color:T.danger, fontSize:'.75rem', cursor:'pointer', width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
              {i===0 && <div style={{ position:'absolute', bottom:3, left:3, fontSize:'.46rem', letterSpacing:'.1em', textTransform:'uppercase', background:`rgba(14,184,160,.9)`, color:'#000', padding:'.12rem .35rem', borderRadius:2, fontWeight:600 }}>Primary</div>}
            </div>
          ))}
        </div>
      )}
      {canAdd ? (
        <div style={{ border:`1.5px dashed #2e2e33`, borderRadius:4, padding:'1.2rem', textAlign:'center', cursor:'pointer', background:'#0d0d0e', transition:'border-color .15s' }}
          onClick={()=>fileRef.current.click()}
          onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
          onMouseLeave={e=>e.currentTarget.style.borderColor='#2e2e33'}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display:'none' }} onChange={handleFiles} />
          <div style={{ fontSize:'1.8rem', opacity:.35, marginBottom:'.3rem' }}>📷</div>
          <p style={{ fontSize:'.7rem', color:'#7a7872', lineHeight:1.6 }}>
            Tap to add {imgs.length===0?'up to 2 photos':'1 more photo'}<br/>
            <span style={{ fontSize:'.6rem', opacity:.5 }}>JPG · PNG · WEBP — max 5MB</span>
          </p>
        </div>
      ) : (
        <div style={{ fontSize:'.65rem', color:'#7a7872', textAlign:'center', padding:'.5rem', border:'1px solid #2e2e33', borderRadius:3, letterSpacing:'.08em' }}>
          MAX 2 PHOTOS — remove one to replace
        </div>
      )}
    </div>
  );
}

// ── Guitar Pick Logo ─────────────────────────────────────────────────────────
function PickLogo({ size=32, color='#0eb8a0' }) {
  // Amp cab with combo lock dial — the GearVault logo
  const d = color + '18';  // dim fill
  const m = color + '44';  // mid opacity
  const t = color + '55';  // tick/detail opacity
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none"
      style={{ flexShrink:0, filter:`drop-shadow(0 0 6px ${color}66)` }}>
      {/* Cabinet */}
      <rect x="8" y="18" width="84" height="72" rx="4" fill="#1a1a1e" stroke={color} strokeWidth="2.5"/>
      {/* Top panel */}
      <rect x="8" y="18" width="84" height="19" rx="4" fill="#0d0d0f" stroke={color} strokeWidth="2.5"/>
      <rect x="8" y="29" width="84" height="8" fill="#0d0d0f"/>
      {/* Knobs */}
      <circle cx="25" cy="27" r="5" fill="#1e1e22" stroke={color} strokeWidth="1.5"/>
      <line x1="25" y1="22.5" x2="25" y2="25" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="50" cy="27" r="5" fill="#1e1e22" stroke={color} strokeWidth="1.5"/>
      <line x1="50" y1="22.5" x2="51.8" y2="24.2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="75" cy="27" r="5" fill="#1e1e22" stroke={color} strokeWidth="1.5"/>
      <line x1="75" y1="22.5" x2="73.2" y2="24.2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      {/* Grille */}
      <rect x="14" y="43" width="72" height="38" rx="2" fill="#0a1a19" stroke={m} strokeWidth="1"/>
      <line x1="14" y1="50" x2="86" y2="50" stroke={d} strokeWidth="1"/>
      <line x1="14" y1="57" x2="86" y2="57" stroke={d} strokeWidth="1"/>
      <line x1="14" y1="64" x2="86" y2="64" stroke={d} strokeWidth="1"/>
      <line x1="14" y1="71" x2="86" y2="71" stroke={d} strokeWidth="1"/>
      {/* Lock outer ring */}
      <circle cx="50" cy="62" r="17" fill="#111112" stroke={color} strokeWidth="2"/>
      {/* Lock face */}
      <circle cx="50" cy="62" r="13" fill="#0d1f1e" stroke={t} strokeWidth="1"/>
      {/* Tick marks */}
      <g stroke={color} strokeWidth="1.2" strokeLinecap="round">
        <line x1="50" y1="50" x2="50" y2="52.5"/>
        <line x1="56.5" y1="51.3" x2="55.2" y2="53.5"/>
        <line x1="61.3" y1="55.5" x2="59.4" y2="56.5"/>
        <line x1="63" y1="62" x2="60.5" y2="62"/>
        <line x1="61.3" y1="68.5" x2="59.4" y2="67.5"/>
        <line x1="56.5" y1="72.7" x2="55.2" y2="70.5"/>
        <line x1="50" y1="74" x2="50" y2="71.5"/>
        <line x1="43.5" y1="72.7" x2="44.8" y2="70.5"/>
        <line x1="38.7" y1="68.5" x2="40.6" y2="67.5"/>
        <line x1="37" y1="62" x2="39.5" y2="62"/>
        <line x1="38.7" y1="55.5" x2="40.6" y2="56.5"/>
        <line x1="43.5" y1="51.3" x2="44.8" y2="53.5"/>
      </g>
      {/* Pointer notch */}
      <polygon points="50,48 48,52 52,52" fill={color}/>
      {/* Spindle */}
      <circle cx="50" cy="62" r="3.5" fill={color} opacity="0.9"/>
      <circle cx="50" cy="62" r="1.5" fill="#111112"/>
      {/* Feet */}
      <rect x="15" y="88" width="8" height="4" rx="2" fill={t}/>
      <rect x="77" y="88" width="8" height="4" rx="2" fill={t}/>
    </svg>
  );
}
// ── Category SVG Icons ────────────────────────────────────────────────────────
function CatIcon({ cat, size=18, color, style={} }) {
  const col = color || CATS[cat]?.color || '#7a7872';
  const s = { display:'block', flexShrink:0, ...style };
  // Guitar / Bass
  if (cat === 'Guitars') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Body */}
      <ellipse cx="9" cy="16" rx="5" ry="5.5" />
      {/* Neck */}
      <line x1="13" y1="12" x2="20" y2="3" />
      {/* Headstock */}
      <line x1="19" y1="3" x2="22" y2="3" />
      <line x1="20" y1="3" x2="20" y2="6" />
      {/* Strings suggestion */}
      <line x1="14.5" y1="13.5" x2="19.5" y2="5" strokeWidth="0.8" opacity="0.6"/>
      {/* Sound hole */}
      <circle cx="9" cy="16" r="1.8" strokeWidth="1"/>
      {/* Waist */}
      <path d="M9 11 C10.5 11.5 13 11 13 12" strokeWidth="1.2"/>
    </svg>
  );
  // Bass guitar — longer neck, asymmetric body, 4 tuning pegs
  if (cat === 'Bass') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Body */}
      <path d="M4 15 C2.5 14.5 2 16 2 17.5 C2 20 4 22 7.5 22 C11 22 13 20 13 17.5 C13 15 11 13.5 9 14" />
      <path d="M9 14 C9 12 9.5 10 10.5 8.5"/>
      {/* Neck */}
      <line x1="10.5" y1="8.5" x2="19" y2="2"/>
      {/* Headstock */}
      <line x1="18" y1="2" x2="22" y2="2"/>
      {/* 4 tuning pegs */}
      <circle cx="19" cy="1.5" r=".8" fill={col} stroke="none"/>
      <circle cx="21.5" cy="1.5" r=".8" fill={col} stroke="none"/>
      <circle cx="19" cy="3.5" r=".8" fill={col} stroke="none"/>
      <circle cx="21.5" cy="3.5" r=".8" fill={col} stroke="none"/>
      {/* Strings */}
      <line x1="12" y1="10" x2="19.5" y2="3" strokeWidth="0.7" opacity="0.5"/>
      {/* Pickup */}
      <rect x="4.5" y="15.5" width="5" height="2" rx=".5" strokeWidth="1" opacity="0.6"/>
    </svg>
  );
  // Amp
  if (cat === 'Amps') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Cabinet box */}
      <rect x="2" y="5" width="20" height="16" rx="1.5"/>
      {/* Speaker cone */}
      <circle cx="12" cy="13" r="5"/>
      <circle cx="12" cy="13" r="2.2"/>
      {/* Top panel */}
      <line x1="2" y1="9" x2="22" y2="9"/>
      {/* Knobs */}
      <circle cx="6" cy="7" r="1" fill={col}/>
      <circle cx="10" cy="7" r="1" fill={col}/>
      <circle cx="14" cy="7" r="1" fill={col}/>
      <circle cx="18" cy="7" r="1" fill={col}/>
    </svg>
  );
  // Pedal
  if (cat === 'Pedals') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Enclosure */}
      <rect x="4" y="6" width="16" height="14" rx="2"/>
      {/* Footswitch */}
      <circle cx="12" cy="16" r="2.5"/>
      {/* Knobs row */}
      <circle cx="8" cy="10" r="1.5"/>
      <circle cx="12" cy="10" r="1.5"/>
      <circle cx="16" cy="10" r="1.5"/>
      {/* Input/output jacks */}
      <line x1="4" y1="13" x2="2" y2="13" strokeWidth="2"/>
      <line x1="20" y1="13" x2="22" y2="13" strokeWidth="2"/>
    </svg>
  );
  // PA / Recording
  if (cat === 'PA' || cat === 'LiveSound') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Mic capsule */}
      <rect x="9" y="2" width="6" height="10" rx="3"/>
      {/* Mic stand arm */}
      <path d="M5 11 C5 17 19 17 19 11"/>
      {/* Stand pole */}
      <line x1="12" y1="17" x2="12" y2="21"/>
      {/* Base */}
      <line x1="9" y1="21" x2="15" y2="21"/>
    </svg>
  );
  // Drums
  if (cat === 'Drums') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Bass drum */}
      <ellipse cx="12" cy="17" rx="8" ry="5"/>
      {/* Bass drum depth */}
      <path d="M4 17 L4 19 Q12 22 20 19 L20 17"/>
      {/* Snare / tom suggestion */}
      <ellipse cx="12" cy="17" rx="4" ry="2.2" strokeWidth="1" opacity="0.5"/>
      {/* Hi-hat cymbal */}
      <ellipse cx="6" cy="8" rx="4" ry="1.2"/>
      <line x1="6" y1="9" x2="6" y2="17"/>
      {/* Ride cymbal */}
      <ellipse cx="19" cy="7" rx="3.5" ry="1" />
    </svg>
  );
  // Keyboard / Synth
  if (cat === 'Keys') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Body */}
      <rect x="2" y="8" width="20" height="10" rx="1.5"/>
      {/* White key dividers */}
      <line x1="5.4" y1="8" x2="5.4" y2="18"/>
      <line x1="8.8" y1="8" x2="8.8" y2="18"/>
      <line x1="12.2" y1="8" x2="12.2" y2="18"/>
      <line x1="15.6" y1="8" x2="15.6" y2="18"/>
      <line x1="19" y1="8" x2="19" y2="18"/>
      {/* Black keys */}
      <rect x="4" y="8" width="2" height="5.5" rx=".5" fill={col} stroke="none"/>
      <rect x="7.4" y="8" width="2" height="5.5" rx=".5" fill={col} stroke="none"/>
      <rect x="13.8" y="8" width="2" height="5.5" rx=".5" fill={col} stroke="none"/>
      <rect x="17.2" y="8" width="2" height="5.5" rx=".5" fill={col} stroke="none"/>
    </svg>
  );
  // Studio Gear
  if (cat === 'Studio') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Rack unit box */}
      <rect x="2" y="5" width="20" height="14" rx="1.5"/>
      {/* Screen/meter area */}
      <rect x="4" y="8" width="8" height="5" rx="1" strokeWidth="1"/>
      {/* Meter bars */}
      <line x1="5.5" y1="12" x2="5.5" y2="9.5" strokeWidth="1.2"/>
      <line x1="7.5" y1="12" x2="7.5" y2="10.5" strokeWidth="1.2"/>
      <line x1="9.5" y1="12" x2="9.5" y2="9" strokeWidth="1.2"/>
      {/* Knobs */}
      <circle cx="15" cy="10" r="1.5"/>
      <circle cx="19" cy="10" r="1.5"/>
      {/* Jack sockets */}
      <circle cx="15" cy="14.5" r="1" fill={col} opacity=".6" stroke="none"/>
      <circle cx="18" cy="14.5" r="1" fill={col} opacity=".6" stroke="none"/>
    </svg>
  );
  // Orchestra — violin silhouette
  if (cat === 'Orchestra') return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={s}>
      {/* Body upper bout */}
      <path d="M9 10 C6 10 4 12 4 14 C4 16.5 6 18 8 18"/>
      {/* Body lower bout */}
      <path d="M8 18 C6 18 4 19.5 4 21.5 C4 23 6 24 8.5 24"/>
      {/* Right side mirror */}
      <path d="M15 10 C18 10 20 12 20 14 C20 16.5 18 18 16 18"/>
      <path d="M16 18 C18 18 20 19.5 20 21.5 C20 23 18 24 15.5 24"/>
      {/* Bottom join */}
      <path d="M8.5 24 C10 25 14 25 15.5 24"/>
      {/* Waist */}
      <path d="M8 15 C9.5 14 14.5 14 16 15"/>
      {/* Neck */}
      <rect x="10.5" y="2" width="3" height="9" rx="1"/>
      {/* Scroll */}
      <path d="M10.5 2 C10 0 12 0 12 2"/>
      {/* F-holes */}
      <line x1="8.5" y1="16" x2="8.5" y2="20" strokeWidth="1"/>
      <line x1="15.5" y1="16" x2="15.5" y2="20" strokeWidth="1"/>
    </svg>
  );
  // Fallback box
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="1.5" strokeLinecap="round" style={s}>
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="9" y1="9" x2="21" y2="9"/>
    </svg>
  );
}


// ── Tag Selector ──────────────────────────────────────────────────────────────
function TagSelector({ tags, onChange, allTags }) {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);

  // Suggestions: all known tags not already selected, filtered by input
  const suggestions = allTags.filter(t =>
    !tags.includes(t) && (!input || t.toLowerCase().includes(input.toLowerCase()))
  );

  function addTag(tag) {
    const clean = tag.trim();
    if (!clean || tags.includes(clean)) return;
    onChange([...tags, clean]);
    setInput('');
  }

  function removeTag(tag) { onChange(tags.filter(t=>t!==tag)); }

  function handleKey(e) {
    if ((e.key==='Enter' || e.key===',') && input.trim()) {
      e.preventDefault();
      addTag(input);
    }
    if (e.key==='Backspace' && !input && tags.length) {
      removeTag(tags[tags.length-1]);
    }
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'.4rem' }}>
      <label style={{ fontSize:'.6rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.14em' }}>Labels / Tags</label>
      {/* Tag pills + input */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:'.35rem', background:'#0d0d0e', border:'1px solid #2e2e33', borderRadius:4, padding:'.5rem .65rem', minHeight:46, alignItems:'center', cursor:'text' }}
        onClick={e=>{ e.currentTarget.querySelector('input')?.focus(); }}>
        {tags.map(tag=>(
          <div key={tag} style={{ display:'flex', alignItems:'center', gap:'.3rem', fontSize:'.65rem', padding:'.2rem .5rem', borderRadius:10, background:'rgba(155,109,255,.15)', border:'1px solid rgba(155,109,255,.3)', color:'#9b6dff' }}>
            {tag}
            <button onClick={()=>removeTag(tag)} style={{ background:'none', border:'none', color:'#9b6dff', cursor:'pointer', fontSize:'.65rem', padding:0, lineHeight:1, opacity:.7 }}>✕</button>
          </div>
        ))}
        <input
          value={input}
          onChange={e=>setInput(e.target.value)}
          onKeyDown={handleKey}
          onFocus={()=>setFocused(true)}
          onBlur={()=>setTimeout(()=>setFocused(false),150)}
          placeholder={tags.length===0?'Add labels: Home Studio, Band Name…':''}
          style={{ flex:1, minWidth:100, background:'transparent', border:'none', outline:'none', color:'#d4d2cc', fontFamily:'inherit', fontSize:'14px', padding:'.1rem 0' }}
          autoCapitalize="words" autoCorrect="off"
        />
      </div>
      {/* Suggestions dropdown */}
      {focused && (suggestions.length>0 || input.trim()) && (
        <div style={{ background:'#18181a', border:'1px solid #2e2e33', borderRadius:3, overflow:'hidden', boxShadow:'0 4px 16px rgba(0,0,0,.5)' }}>
          {suggestions.slice(0,6).map(tag=>(
            <div key={tag} onMouseDown={()=>addTag(tag)}
              style={{ padding:'.5rem .75rem', fontSize:'.72rem', color:'#d4d2cc', cursor:'pointer', borderBottom:'1px solid #1e1e22' }}
              onMouseEnter={e=>e.currentTarget.style.background='#222226'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              {tag}
            </div>
          ))}
          {input.trim() && !allTags.includes(input.trim()) && (
            <div onMouseDown={()=>addTag(input)}
              style={{ padding:'.5rem .75rem', fontSize:'.72rem', color:'#9b6dff', cursor:'pointer', borderTop:suggestions.length?'1px solid #2e2e33':'none' }}
              onMouseEnter={e=>e.currentTarget.style.background='#222226'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              + Create "<strong>{input.trim()}</strong>"
            </div>
          )}
        </div>
      )}
      <div style={{ fontSize:'.56rem', color:'#5a5a5a', lineHeight:1.5 }}>Press Enter or comma to add. Use for locations (Home Studio, Stage), bands, owners, etc.</div>
    </div>
  );
}

// ── GearForm ──────────────────────────────────────────────────────────────────
function GearForm({ initial, onSave, onCancel, title, saving, onReverbLookup }) {
  const isMob = useIsMobile();
  const imgLoadRef = useRef(false); // cancels background image loader on unmount
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial, imgs: initial.imgs||(initial.img?[initial.img]:[]), tags: initial.tags||[], rv: initial.rv||'' });
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  function handleSubmit() {
    if(!form.make.trim()||!form.model.trim()){ alert('Make and Model are required.'); return; }
    onSave(form);
  }
  const modalStyle = isMob
    ? { position:'fixed', inset:0, background:'#18181a', display:'flex', flexDirection:'column', zIndex:1001, overflowY:'auto', paddingBottom:'env(safe-area-inset-bottom,1rem)' }
    : { background:'#18181a', border:'1px solid #2e2e33', borderRadius:4, width:'100%', maxWidth:580, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.9)' };
  return (
    <div style={modalStyle}>
      <div style={{ padding:isMob?'1rem 1rem .8rem':'1.1rem 1.4rem', borderBottom:'1px solid #2e2e33', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#1f1f22', position:'sticky', top:0, zIndex:2, paddingTop:isMob?'calc(env(safe-area-inset-top,0px) + 1rem)':'1.1rem' }}>
        <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.15rem', letterSpacing:'.06em', color:'#f0ede6' }} dangerouslySetInnerHTML={{ __html:title }} />
        <Btn variant="ghost" onClick={onCancel} style={{ fontSize:'1.1rem', minWidth:44, minHeight:44 }}>✕</Btn>
      </div>
      <div style={{ padding:isMob?'1rem':'1.35rem', display:'flex', flexDirection:'column', gap:'1rem', flex:1 }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.85rem' }}>
          <Field label="Category *">
            <select style={inputStyle} value={form.cat} onChange={e=>set('cat',e.target.value)}>
              {Object.entries(CATS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Condition">
            <select style={inputStyle} value={form.cond} onChange={e=>set('cond',e.target.value)}>
              {CONDITIONS.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.85rem' }}>
          <Field label="Make *"><input style={inputStyle} value={form.make} onChange={e=>set('make',e.target.value)} placeholder="Fender, Boss…" autoCapitalize="words" /></Field>
          <Field label="Model *"><input style={inputStyle} value={form.model} onChange={e=>set('model',e.target.value)} placeholder="Stratocaster…" autoCapitalize="words" /></Field>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.85rem' }}>
          <Field label="Year"><input style={inputStyle} value={form.yr} onChange={e=>set('yr',e.target.value)} placeholder="2019" inputMode="numeric" /></Field>
          <Field label="Purchase Price ($)"><input style={inputStyle} type="number" inputMode="decimal" value={form.paid} onChange={e=>set('paid',e.target.value)} placeholder="0.00" /></Field>
        </div>
        <Field label="Serial Number">
          <div style={{ display:'flex', gap:'.5rem' }}>
            <input style={{...inputStyle, flex:1}} value={form.sn} onChange={e=>set('sn',e.target.value)} placeholder="MX19012345" autoCapitalize="characters" />
            <Btn variant="teal" size="sm"
              disabled={!form.sn}
              onClick={()=>{
                const r = decodeSerial(form.make, form.sn);
                if (r && typeof r.year === 'number') {
                  set('yr', String(r.year));
                } else if (r && typeof r.year === 'string') {
                  const firstYr = r.year.match(/\d{4}/);
                  if (firstYr) set('yr', firstYr[0]);
                } else {
                  alert('No date found — check that Make is set correctly (e.g. Fender, Gibson, Martin).');
                }
              }}
              style={{ flexShrink:0, whiteSpace:'nowrap' }}>📅 Date It</Btn>
          </div>
        </Field>

        {(form.cat==='Guitars'||form.cat==='Bass') && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.58rem', color:T.accent, textTransform:'uppercase', letterSpacing:'.18em' }}>
              <div style={{ flex:1, height:1, background:'#2e2e33' }} /> 🎸 Guitar Setup <div style={{ flex:1, height:1, background:'#2e2e33' }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.85rem' }}>
              <Field label="Primary Tuning">
                <select style={inputStyle} value={form.tuning} onChange={e=>set('tuning',e.target.value)}>
                  <option value="">— Select —</option>
                  {TUNING_GROUPS.map(g=>(
                    <optgroup key={g.group} label={g.group}>
                      {g.options.map(t=><option key={t} value={t}>{t}</option>)}
                    </optgroup>
                  ))}
                </select>
              </Field>
              <Field label="String Gauge">
                <select style={inputStyle}
                  value={STRING_GAUGE_GROUPS.flatMap(g=>g.options).some(o=>o.value===form.gauge)?form.gauge:(form.gauge?'__custom__':'')}
                  onChange={e=>{ if(e.target.value==='__custom__') set('gauge',''); else set('gauge',e.target.value); }}>
                  <option value="">— Select —</option>
                  {STRING_GAUGE_GROUPS.map(grp=>(
                    <optgroup key={grp.group} label={grp.group}>
                      {grp.options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                {/* Custom gauge text input — shown when value isn't in presets */}
                {form.gauge!=='' && !STRING_GAUGE_GROUPS.flatMap(g=>g.options).some(o=>o.value===form.gauge) && (
                  <input style={{...inputStyle, marginTop:'.4rem', fontSize:'16px'}}
                    placeholder="e.g. .009 .011 .016 .026 .036 .046"
                    value={form.gauge}
                    onChange={e=>set('gauge',e.target.value)} />
                )}
              </Field>
            </div>
          </>
        )}

        {form.cat==='Pedals' && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.58rem', color:T.info, textTransform:'uppercase', letterSpacing:'.18em' }}>
              <div style={{ flex:1, height:1, background:'#2e2e33' }} /> 🎛 Pedal Type <div style={{ flex:1, height:1, background:'#2e2e33' }} />
            </div>
            <Field label="Effect Type">
              <select style={inputStyle} value={form.pedal_type} onChange={e=>set('pedal_type',e.target.value)}>
                <option value="">— Select type —</option>
                {PEDAL_TYPES.map(g=>(
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map(t=><option key={t} value={t}>{t}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>
          </>
        )}

        {form.cat==='LiveSound' && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.58rem', color:'#39d98a', textTransform:'uppercase', letterSpacing:'.18em' }}>
              <div style={{ flex:1, height:1, background:'#2e2e33' }} /> 🎤 Live Sound Type <div style={{ flex:1, height:1, background:'#2e2e33' }} />
            </div>
            <Field label="Equipment Type">
              <select style={inputStyle} value={form.live_type} onChange={e=>set('live_type',e.target.value)}>
                <option value="">— Select type —</option>
                {LIVE_TYPES.map(g=>(
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map(t=><option key={t} value={t}>{t}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>
          </>
        )}

        {form.cat==='Orchestra' && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.58rem', color:'#d4a017', textTransform:'uppercase', letterSpacing:'.18em' }}>
              <div style={{ flex:1, height:1, background:'#2e2e33' }} /> 🎻 Orchestra <div style={{ flex:1, height:1, background:'#2e2e33' }} />
            </div>
            <div style={{ display:'flex', gap:'.6rem' }}>
              <Field label="Section">
                <select style={inputStyle} value={form.orch_section} onChange={e=>{ set('orch_section', e.target.value); set('orch_instrument',''); }}>
                  <option value="">— Section —</option>
                  {ORCHESTRA_SECTIONS.map(s=>(
                    <option key={s.section} value={s.section}>{s.section}</option>
                  ))}
                </select>
              </Field>
              <Field label="Instrument">
                <select style={inputStyle} value={form.orch_instrument} onChange={e=>set('orch_instrument', e.target.value)} disabled={!form.orch_section}>
                  <option value="">— Instrument —</option>
                  {(ORCHESTRA_SECTIONS.find(s=>s.section===form.orch_section)?.instruments||[]).map(i=>(
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </Field>
            </div>
          </>
        )}

        {/* Tags */}
        <TagSelector tags={form.tags} onChange={v=>set('tags',v)} allTags={initial._allTags||[]} />

        <Field label="Notes / Mods / Accessories">
          <textarea style={{...inputStyle, resize:'vertical', minHeight:72, fontSize:'16px'}} value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Case, mods, provenance…" />
        </Field>
        <div style={{ display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.58rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.18em' }}>
          <div style={{ flex:1, height:1, background:'#2e2e33' }} /> Photos (max 2) <div style={{ flex:1, height:1, background:'#2e2e33' }} />
        </div>
        <ImageUploader imgs={form.imgs} onChange={v=>set('imgs',typeof v==='function'?v(form.imgs):v)} />
        {/* Reverb + Value row */}
        <div style={{ display:'flex', alignItems:'center', gap:'.5rem', fontSize:'.58rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.18em' }}>
          <div style={{ flex:1, height:1, background:'#2e2e33' }} /> Market Value <div style={{ flex:1, height:1, background:'#2e2e33' }} />
        </div>
        <Field label="Reverb Search Terms — optional">
          <div style={{ display:'flex', gap:'.5rem' }}>
            <input style={{...inputStyle, flex:1}} value={form.reverb_query} onChange={e=>set('reverb_query',e.target.value)} placeholder={`${form.make} ${form.model}${form.yr?' '+form.yr:''}`.trim()||'Make Model Year…'} autoCapitalize="none" autoCorrect="off" />
            <Btn variant="teal" size="sm" onClick={()=>onReverbLookup&&onReverbLookup(form, v=>set('rv',v))} style={{ flexShrink:0, whiteSpace:'nowrap' }}>↺ Lookup</Btn>
          </div>
          <div style={{ fontSize:'.56rem', color:'#5a5a5a', marginTop:'.18rem', lineHeight:1.5 }}>Refine the search if the auto-built query returns wrong results (e.g. add "MIM" or "reissue").</div>
        </Field>
        <div style={{ display:'flex', gap:'.5rem', alignItems:'flex-end' }}>
          <Field label="Reverb Estimate">
            <input style={{...inputStyle, color: form.rv ? T.accent : '#7a7872'}} type="number" inputMode="decimal" value={form.rv||''} readOnly placeholder="— run lookup —" />
          </Field>
          <Field label="Manual Override ($)">
            <input style={inputStyle} type="number" inputMode="decimal" value={form.mv} onChange={e=>set('mv',e.target.value)} placeholder="optional" />
          </Field>
        </div>
      </div>
      <div style={{ padding:isMob?'.85rem 1rem':'.88rem 1.35rem', borderTop:'1px solid #2e2e33', background:'#1f1f22', display:'flex', gap:'.65rem', justifyContent:'flex-end', position:'sticky', bottom:0, zIndex:2 }}>
        <Btn onClick={onCancel} disabled={saving} style={{ flex:isMob?1:0 }}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSubmit} disabled={saving} style={{ flex:isMob?2:0, position:'relative' }}>
          {saving
            ? <span style={{ display:'flex', alignItems:'center', gap:'.5rem' }}>
                <span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%',
                  border:'2px solid rgba(0,0,0,.3)', borderTopColor:'#000',
                  animation:'spin .7s linear infinite' }}/>
                Adding Gear…
              </span>
            : 'Save Gear'}
        </Btn>
      </div>
    </div>
  );
}

// ── Compact GearCard (grid tile) ──────────────────────────────────────────────
function GearCard({ item, onEdit, onDelete, onView, onReverb }) {
  const cat = CATS[item.cat]||{ label:item.cat, color:'#7a7872' };
  const val = item.rv||item.mv;
  const [lightbox, setLightbox] = useState(null);
  const imgs = item.imgs||(item.img?[item.img]:[]);
  const primaryImg = imgs[0]||null;

  return (
    <>
      <div style={{ background:'#18181a', border:'1px solid #2e2e33', borderRadius:6, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,.5)', WebkitTapHighlightColor:'transparent', display:'flex', flexDirection:'column' }}>
        {/* Top color bar */}
        <div style={{ height:2, background:cat.color, flexShrink:0 }} />

        {/* Photo — shorter on compact grid */}
        <div style={{ width:'100%', height:130, background:'#0d0d0e', position:'relative', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', cursor:primaryImg?'zoom-in':'pointer', flexShrink:0 }}
          onClick={()=>{ if(primaryImg) setLightbox(0); else onEdit(item.id); }}>
          {primaryImg
            ? <img src={primaryImg} alt={item.model} style={{ width:'100%', height:'100%', objectFit:'cover' }} loading="lazy" />
            : <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'.45rem' }}>
                <CatIcon cat={item.cat} size={52} color={cat.color} style={{ opacity:.22 }} />
                <div style={{ fontSize:'.5rem', color:cat.color, opacity:.35, textTransform:'uppercase', letterSpacing:'.14em' }}>{cat.label}</div>
              </div>
          }
          {/* Reverb badge */}
          <div style={{ position:'absolute', top:'.45rem', right:'.45rem', fontSize:'.52rem', padding:'.15rem .42rem', borderRadius:2, background:'rgba(0,0,0,.82)', backdropFilter:'blur(4px)', color:item.pend?T.warn:item.rv?T.ok:'#555', border:`1px solid ${item.pend?'rgba(176,106,240,.3)':item.rv?'rgba(57,217,138,.25)':'#2a2a2a'}` }}>
            {item.pend?'…':item.rv?'✓':'—'}
          </div>
          {/* Second image pip */}
          {imgs.length>1 && (
            <div style={{ position:'absolute', bottom:'.4rem', left:'.4rem', cursor:'zoom-in' }} onClick={e=>{e.stopPropagation();setLightbox(1);}}>
              <img src={imgs[1]} alt="" style={{ width:36, height:36, objectFit:'cover', borderRadius:2, border:`2px solid ${T.accent}`, display:'block' }} />
            </div>
          )}
        </div>

        {/* Info */}
        <div style={{ padding:'.65rem .75rem', flex:1, display:'flex', flexDirection:'column', gap:'.4rem' }}>
          {/* Make — big, bold, white (focal point) / Model — small, muted below */}
          <div>
            <div style={{ fontFamily:"'Barlow Condensed','Arial Narrow',sans-serif", fontWeight:700, fontSize:'1rem', color:'#ffffff', lineHeight:1.15 }}>
              {item.make}
            </div>
            <div style={{ fontSize:'.5rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.14em', lineHeight:1, marginTop:'.1rem' }}>
              {item.model}{item.yr&&<span style={{ fontWeight:300 }}> · {item.yr}</span>}
            </div>
          </div>

          {/* Tags */}
          {(item.tags||[]).length>0 && (
            <div style={{ display:'flex', gap:'.3rem', flexWrap:'wrap' }}>
              {(item.tags||[]).map(tag=>(
                <div key={tag} style={{ fontSize:'.5rem', padding:'.12rem .38rem', borderRadius:10, background:'rgba(155,109,255,.12)', border:'1px solid rgba(155,109,255,.2)', color:T.accent2, letterSpacing:'.06em' }}>{tag}</div>
              ))}
            </div>
          )}

          {/* Value */}
          <div style={{ marginTop:'auto', paddingTop:'.35rem', borderTop:'1px solid #1e1e22' }}>
            <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'.95rem', color:item.pend?T.accent2:val?T.accent:'#444', marginBottom:'.4rem' }}>
              {item.pend?'…':val?fmtMoney(parseFloat(val)):'—'}
            </div>
            {/* Buttons below value, full width on mobile */}
            <div style={{ display:'flex', gap:'.3rem' }}>
              <Btn size="xs" variant="teal" onClick={()=>onReverb(item.id)} style={{ minHeight:34, padding:'.2rem .45rem' }}>↺</Btn>
              <Btn size="xs" variant="purple" onClick={()=>onView(item.id)} style={{ flex:1, minHeight:34, padding:'.2rem .5rem', fontSize:'.63rem', letterSpacing:'.05em', fontWeight:700 }}>View / Edit</Btn>
              <Btn size="xs" variant="danger" onClick={()=>onDelete(item.id)} style={{ minHeight:34, padding:'.2rem .42rem' }}>✕</Btn>
            </div>
          </div>
        </div>
      </div>
      {lightbox!==null && <Lightbox imgs={imgs} startIdx={lightbox} onClose={()=>setLightbox(null)} />}
    </>
  );
}


// ── GearRow (list view) ────────────────────────────────────────────────────────
function GearRow({ item, onEdit, onDelete, onView, onReverb }) {
  const cat = CATS[item.cat]||{ label:item.cat, color:'#7a7872' };
  const val = item.rv||item.mv;
  return (
    <div style={{ background:'#18181a', border:'1px solid #2e2e33', borderRadius:4,
      display:'flex', alignItems:'center', gap:'.75rem', padding:'.5rem .75rem',
      borderLeft:`3px solid ${cat.color}`, WebkitTapHighlightColor:'transparent',
      cursor:'pointer' }}
      onClick={()=>onView(item.id)}>
      {/* Cat icon */}
      <div style={{ flexShrink:0, opacity:.5 }}>
        <CatIcon cat={item.cat} size={16} color={cat.color}/>
      </div>
      {/* Make + Model */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:'.58rem', color:'#7a7872', letterSpacing:'.1em', lineHeight:1, textTransform:'uppercase' }}>{item.make}</div>
        <div style={{ fontFamily:"'Barlow Condensed','Arial Narrow',sans-serif", fontWeight:600,
          fontSize:'.9rem', color:'#ffffff', lineHeight:1.2, whiteSpace:'nowrap',
          overflow:'hidden', textOverflow:'ellipsis' }}>{item.model}{item.yr&&<span style={{ fontWeight:300, color:'#5a5a5a', fontSize:'.75rem' }}> {item.yr}</span>}</div>
      </div>
      {/* Tags */}
      {(item.tags||[]).length>0 && (
        <div style={{ display:'flex', gap:'.25rem', flexShrink:0, maxWidth:120, overflow:'hidden' }}>
          {(item.tags||[]).slice(0,2).map(tag=>(
            <div key={tag} style={{ fontSize:'.45rem', padding:'.1rem .3rem', borderRadius:8,
              background:'rgba(155,109,255,.12)', border:'1px solid rgba(155,109,255,.2)',
              color:'#9b6dff', whiteSpace:'nowrap' }}>{tag}</div>
          ))}
        </div>
      )}
      {/* Value */}
      <div style={{ fontFamily:"'Black Ops One',sans-serif", fontSize:'.8rem',
        color:val?'#0eb8a0':'#333', flexShrink:0, minWidth:64, textAlign:'right' }}>
        {val?fmtMoney(parseFloat(val)):'—'}
      </div>
      {/* Buttons — stopPropagation so they don't trigger the row click */}
      <div style={{ display:'flex', gap:'.25rem', flexShrink:0 }} onClick={e=>e.stopPropagation()}>
        <Btn size="xs" variant="teal"   onClick={()=>onReverb(item.id)} style={{ minHeight:30, padding:'.15rem .4rem' }}>↺</Btn>
        <Btn size="xs" variant="danger" onClick={()=>onDelete(item.id)} style={{ minHeight:30, padding:'.15rem .4rem' }}>✕</Btn>
      </div>
    </div>
  );
}

// ── ViewModal ─────────────────────────────────────────────────────────────────
function ViewModal({ item, onClose, onEdit, onDelete, onReverb }) {
  const [lightbox, setLightbox] = useState(null);
  const [sharing, setSharing] = useState(false);
  const isMob = useIsMobile();
  if(!item) return null;
  const cat=CATS[item.cat]||{ label:item.cat, color:'#7a7872' };
  const val=item.rv||item.mv;
  const imgs=item.imgs||(item.img?[item.img]:[]);
  const rows=[
    ['Category',cat.label],['Condition',item.cond||'—'],
    ['Make',item.make],['Model',item.model+(item.yr?` (${item.yr})`:' ')],
    ['Serial #',item.sn||'—'],['Purchase Price',item.paid?fmtMoney(parseFloat(item.paid)):'—'],
    ['Est. Value',val?fmtMoney(parseFloat(val))+(item.rv?` · Reverb (${item.rvc||0})`:' · manual'):'—'],
    ['Added',new Date(item.created_at||Date.now()).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})],
    ...(item.cat==='Pedals'&&item.pedal_type?[['Effect Type',item.pedal_type]]:[]),
    ...(item.cat==='LiveSound'&&item.live_type?[['Equipment Type',item.live_type]]:[]),
    ...(item.cat==='Orchestra'&&item.orch_section?[['Section',item.orch_section]]:[]),
    ...(item.cat==='Orchestra'&&item.orch_instrument?[['Instrument',item.orch_instrument]]:[]),
    ...((item.cat==='Guitars'||item.cat==='Bass')&&item.tuning?[['Primary Tuning',item.tuning]]:[]),
    ...((item.cat==='Guitars'||item.cat==='Bass')&&item.gauge ?[['String Gauge',item.gauge]]:[]  ),
  ];
  const itemTags = item.tags||[];
  const containerStyle=isMob
    ?{ position:'fixed', inset:0, background:'#18181a', display:'flex', flexDirection:'column', zIndex:1001, overflowY:'auto', paddingBottom:'env(safe-area-inset-bottom,1rem)' }
    :{ background:'#18181a', border:'1px solid #2e2e33', borderRadius:4, width:'100%', maxWidth:510, maxHeight:'92vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.9)' };
  const wrapStyle=isMob
    ?{ position:'fixed', inset:0, zIndex:1000 }
    :{ position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' };
  return (
    <>
      <div style={wrapStyle} onClick={isMob?undefined:e=>{ if(e.target===e.currentTarget) onClose(); }}>
        <div style={containerStyle}>
          <div style={{ padding:isMob?'1rem':'1.1rem 1.4rem', borderBottom:'1px solid #2e2e33', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#1f1f22', position:'sticky', top:0, paddingTop:isMob?'calc(env(safe-area-inset-top,0px) + 1rem)':'1.1rem' }}>
            <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.15rem', color:'#f0ede6' }}>{item.make} <em style={{ color:T.accent, fontStyle:'normal' }}>{item.model}</em></div>
            <Btn variant="ghost" onClick={onClose} style={{ fontSize:'1.1rem', minWidth:44, minHeight:44 }}>✕</Btn>
          </div>
          <div style={{ padding:isMob?'1rem':'1.35rem', display:'flex', flexDirection:'column', gap:'1rem', flex:1 }}>
            {imgs.length>0 && (
              <div style={{ display:'flex', gap:'.6rem' }}>
                {imgs.map((src,i)=>(
                  <div key={i} onClick={()=>setLightbox(i)}
                    style={{ flex:i===0?'1 1 auto':'0 0 90px', height:i===0?210:90, overflow:'hidden', borderRadius:4, border:'1px solid #2e2e33', cursor:'pointer', position:'relative' }}>
                    <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.7rem 1rem' }}>
              {rows.map(([k,v])=>(
                <div key={k}>
                  <div style={{ fontSize:'.58rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.13em', marginBottom:'.15rem' }}>{k}</div>
                  <div style={{ fontSize:'.82rem', color:'#d4d2cc', lineHeight:1.3 }}>{v}</div>
                </div>
              ))}
            </div>
            {itemTags.length>0 && (
              <div style={{ display:'flex', gap:'.4rem', flexWrap:'wrap' }}>
                {itemTags.map(tag=>(
                  <div key={tag} style={{ fontSize:'.65rem', padding:'.22rem .6rem', borderRadius:12, background:'rgba(155,109,255,.12)', border:'1px solid rgba(155,109,255,.25)', color:T.accent2 }}>{tag}</div>
                ))}
              </div>
            )}
            {item.notes && (
              <div style={{ background:'#0d0d0e', border:'1px solid #2e2e33', borderRadius:3, padding:'.75rem .85rem', fontSize:'.75rem', color:'#7a7872', lineHeight:1.7 }}>
                <div style={{ fontSize:'.58rem', textTransform:'uppercase', letterSpacing:'.13em', marginBottom:'.35rem', color:'#5a5a5a' }}>Notes</div>
                {item.notes}
              </div>
            )}
          </div>
          <div style={{ padding:isMob?'.85rem 1rem':'.88rem 1.35rem', borderTop:'1px solid #2e2e33', background:'#1f1f22', display:'flex', gap:'.6rem', position:'sticky', bottom:0 }}>
            <Btn size="sm" variant="teal" onClick={()=>{onClose();onReverb(item.id);}} style={{ flex:1, minHeight:46 }}>↺ Reverb</Btn>
            {isMob && typeof navigator.share === 'function' && (
              <Btn size="sm" variant="teal" disabled={sharing} onClick={async ()=>{
                setSharing(true);
                try {
                  const file = await generateShareImage(item);
                  const canShareFiles = navigator.canShare && navigator.canShare({ files:[file] });
                  if (canShareFiles) {
                    await navigator.share({ title:`${item.make} ${item.model}`, files:[file] });
                  } else {
                    // fallback: share as text if file sharing not supported
                    await navigator.share({ title:`${item.make} ${item.model}`, text:`${item.make} ${item.model}${item.yr?' ('+item.yr+')':''}` });
                  }
                } catch(e) { if(e.name!=='AbortError') console.warn('Share failed',e); }
                finally { setSharing(false); }
              }} style={{ flexShrink:0, minHeight:46, minWidth:46, padding:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {sharing ? <span style={{fontSize:'.8rem'}}>…</span> : <ShareIcon size={19} color="currentColor"/>}
              </Btn>
            )}
            <Btn size="sm" variant="silver" onClick={()=>{onClose();onEdit(item.id);}} style={{ flex:1, minHeight:46 }}>Edit</Btn>
            <Btn size="sm" variant="danger" onClick={()=>{onClose();onDelete(item.id);}} style={{ flex:1, minHeight:46 }}>Delete</Btn>
          </div>
        </div>
      </div>
      {lightbox!==null && <Lightbox imgs={imgs} startIdx={lightbox} onClose={()=>setLightbox(null)} />}
    </>
  );
}

// ── Site Footer (shared across all pages) ────────────────────────────────────
function SiteFooter({ onPrivacy }) {
  return (
    <div style={{ borderTop:'1px solid #1e1e20', marginTop:'2rem', padding:'1.25rem 0 .75rem', textAlign:'center' }}>
      <div style={{ fontSize:'.58rem', fontFamily:"'Black Ops One',sans-serif", color:'#2e2e33', letterSpacing:'.18em', marginBottom:'.45rem' }}>
        GEAR<span style={{ color:'#0eb8a0' }}>VAULT</span>
      </div>
      <div style={{ fontSize:'.55rem', color:'#3a3a3a', letterSpacing:'.08em', marginBottom:'.85rem' }}>
        gearvault.xyz · Built for musicians, by musicians.
      </div>
      {/* Donate */}
      <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" style={{
        display:'inline-flex', alignItems:'center', gap:'.45rem',
        background:'#1f1a10', border:'1px solid #5a3e00',
        color:'#f5a623', borderRadius:20,
        padding:'.38rem .9rem', marginBottom:'.75rem',
        fontSize:'.62rem', fontFamily:"'Share Tech Mono',monospace",
        letterSpacing:'.08em', textDecoration:'none', textTransform:'uppercase',
        transition:'all .15s', boxShadow:'0 0 12px rgba(245,166,35,.08)',
      }}
        onMouseOver={e=>{ e.currentTarget.style.background='#2a2010'; e.currentTarget.style.boxShadow='0 0 16px rgba(245,166,35,.2)'; }}
        onMouseOut={e=>{ e.currentTarget.style.background='#1f1a10'; e.currentTarget.style.boxShadow='0 0 12px rgba(245,166,35,.08)'; }}
      >
        <span style={{ fontSize:'.85rem' }}>☕</span> Buy Me a Coffee
      </a>
      <div/>
      <button onClick={onPrivacy} style={{
        background:'none', border:'none', cursor:'pointer',
        fontSize:'.52rem', color:'#4a4a4a', letterSpacing:'.1em',
        textTransform:'uppercase', textDecoration:'underline',
        fontFamily:"'Share Tech Mono',monospace", padding:0,
        transition:'color .15s',
      }}
        onMouseOver={e=>e.target.style.color='#7a7872'}
        onMouseOut={e=>e.target.style.color='#4a4a4a'}
      >Privacy Policy</button>
    </div>
  );
}

// ── Privacy Policy Modal ──────────────────────────────────────────────────────
function PrivacyModal({ onClose }) {
  const isMob = useIsMobile();
  const wrapStyle = isMob
    ? { position:'fixed', inset:0, zIndex:1100 }
    : { position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' };
  const containerStyle = isMob
    ? { position:'fixed', inset:0, background:'#18181a', display:'flex', flexDirection:'column', overflowY:'auto', paddingBottom:'env(safe-area-inset-bottom,1.5rem)' }
    : { background:'#18181a', border:'1px solid #2e2e33', borderRadius:4, width:'100%', maxWidth:560, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.9)' };
  const p = { fontSize:'.74rem', color:'#7a7872', lineHeight:1.8, marginBottom:'.75rem' };
  const h = { fontFamily:"'Black Ops One',sans-serif", fontSize:'.82rem', color:'#c0bdb6', marginBottom:'.4rem', marginTop:'1.2rem', letterSpacing:'.04em' };
  const D = { height:1, background:'#1e1e20', margin:'1rem 0' };
  const EFFECTIVE = 'March 9, 2025';

  return (
    <div style={wrapStyle} onClick={isMob ? undefined : e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={{ padding:isMob?'1rem':'1.1rem 1.4rem', borderBottom:'1px solid #2e2e33', background:'#1f1f22',
          display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0,
          paddingTop:isMob?'calc(env(safe-area-inset-top,0px) + 1rem)':'1.1rem' }}>
          <div style={{ fontFamily:"'Black Ops One',sans-serif", fontSize:'1.1rem', color:'#f0ede6' }}>
            Privacy <span style={{ color:'#0eb8a0' }}>Policy</span>
          </div>
          <Btn variant="ghost" onClick={onClose} style={{ fontSize:'1.1rem', minWidth:44, minHeight:44 }}>✕</Btn>
        </div>

        <div style={{ padding:isMob?'1.1rem 1rem':'1.35rem 1.4rem' }}>
          <p style={{ ...p, color:'#5a5a5a', fontSize:'.65rem' }}>Effective date: {EFFECTIVE}</p>

          <p style={p}>
            GearVault ("we", "us", or "our") operates gearvault.xyz. This policy describes what information
            we collect, how we use it, and the rights you have over it. By using GearVault, you agree to the
            practices described here.
          </p>

          <div style={D}/>

          <div style={h}>1. Information We Collect</div>
          <p style={p}>
            <strong style={{ color:'#c0bdb6' }}>Account information.</strong> When you create an account,
            we collect your email address and a hashed password. We never store your password in plain text.
          </p>
          <p style={p}>
            <strong style={{ color:'#c0bdb6' }}>Gear inventory data.</strong> All gear records you create —
            including make, model, photos, notes, valuations, serial numbers, and tags — are stored in your
            private vault and associated with your account.
          </p>
          <p style={p}>
            <strong style={{ color:'#c0bdb6' }}>Usage data.</strong> We do not use analytics services,
            advertising trackers, or third-party data collection of any kind. We do not log individual
            page views or user actions beyond what is necessary to operate the service.
          </p>

          <div style={D}/>

          <div style={h}>2. How We Use Your Information</div>
          <p style={p}>
            We use the information you provide solely to operate GearVault for you. Specifically:
          </p>
          {[
            'To authenticate your identity and secure access to your vault.',
            'To store, retrieve, and display your gear inventory.',
            'To enable features you explicitly use, such as Reverb.com market lookups.',
            'To send transactional emails, such as password reset links, when you request them.',
          ].map((item, i) => (
            <p key={i} style={{ ...p, paddingLeft:'1rem', marginBottom:'.4rem' }}>• {item}</p>
          ))}
          <p style={{ ...p, marginTop:'.6rem' }}>
            <strong style={{ color:'#c0bdb6' }}>We will never use your data for any other purpose.</strong> We
            do not analyze your inventory, sell insights, run ads, or share your information with any third party
            for any reason.
          </p>

          <div style={D}/>

          <div style={h}>3. Data Sharing</div>
          <p style={p}>
            We do not sell, rent, trade, or otherwise share your personal data with any third party, ever.
            Your gear inventory is private to your account. No other user, company, advertiser, or AI system
            has access to your data.
          </p>
          <p style={p}>
            The only third-party service your data touches is our database infrastructure provider (Supabase),
            which stores your data under strict data processing agreements and does not use it for any purpose
            beyond storage and retrieval on our behalf.
          </p>

          <div style={D}/>

          <div style={h}>4. Data Retention</div>
          <p style={p}>
            Your data is retained for as long as your account is active. If you delete your account,
            all associated data — including your gear inventory, photos, and account credentials — is
            permanently deleted within 30 days. We do not retain backups of deleted user data beyond
            that window.
          </p>

          <div style={D}/>

          <div style={h}>5. Security</div>
          <p style={p}>
            All data is transmitted over HTTPS. Passwords are hashed using industry-standard algorithms
            and are never stored in recoverable form. Access to your vault is protected by authenticated
            sessions using secure tokens. We take reasonable technical measures to protect your data
            against unauthorized access.
          </p>

          <div style={D}/>

          <div style={h}>6. Your Rights</div>
          <p style={p}>You have the right to:</p>
          {[
            'Access all data stored in your account at any time by logging in.',
            'Export your gear inventory at any time using the Export Vault feature.',
            'Request deletion of your account and all associated data.',
            'Correct any inaccurate information in your account.',
          ].map((item, i) => (
            <p key={i} style={{ ...p, paddingLeft:'1rem', marginBottom:'.4rem' }}>• {item}</p>
          ))}
          <p style={{ ...p, marginTop:'.6rem' }}>
            To exercise any of these rights, contact us at the address below.
          </p>

          <div style={D}/>

          <div style={h}>7. Cookies</div>
          <p style={p}>
            GearVault uses browser localStorage to store your authentication session token locally on your
            device. This is strictly functional and is never used for tracking or advertising purposes.
            We do not use cookies or any other tracking technologies.
          </p>

          <div style={D}/>

          <div style={h}>8. Children's Privacy</div>
          <p style={p}>
            GearVault is not directed at children under the age of 13. We do not knowingly collect
            personal information from children. If you believe a child has created an account, please
            contact us and we will delete it promptly.
          </p>

          <div style={D}/>

          <div style={h}>9. Changes to This Policy</div>
          <p style={p}>
            We may update this policy from time to time. Any changes will be posted on this page with
            an updated effective date. Your continued use of GearVault after changes constitutes acceptance
            of the updated policy.
          </p>

          <div style={D}/>

          <div style={h}>10. Contact</div>
          <p style={p}>
            If you have questions or concerns about this privacy policy, or wish to exercise your data rights,
            please contact us at: <span style={{ color:'#0eb8a0' }}>privacy@gearvault.xyz</span>
          </p>

          <div style={{ height:1, background:'#1e1e20', margin:'1.5rem 0 1rem' }}/>
          <p style={{ fontSize:'.58rem', color:'#2e2e33', textAlign:'center', lineHeight:1.8 }}>
            GearVault · gearvault.xyz<br/>
            This policy is provided in plain English, not legalese, because we believe you deserve to understand it.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Gear Donut SVG only (recomputes paths at its own size) ───────────────────
function GearDonutSVG({ cats, total, onSliceClick }) {
  const [hov, setHov] = useState(null);
  const DS=120, R=46, r=28, cx=60, cy=60;
  let cumA = -Math.PI/2;
  const slices = cats.map(c=>{
    const angle = (c.count/total)*Math.PI*2;
    const x1=cx+R*Math.cos(cumA), y1=cy+R*Math.sin(cumA);
    cumA += angle;
    const x2=cx+R*Math.cos(cumA), y2=cy+R*Math.sin(cumA);
    const ix1=cx+r*Math.cos(cumA-angle), iy1=cy+r*Math.sin(cumA-angle);
    const ix2=cx+r*Math.cos(cumA), iy2=cy+r*Math.sin(cumA);
    const large=angle>Math.PI?1:0;
    return { ...c, path:`M${x1.toFixed(2)},${y1.toFixed(2)} A${R},${R} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} L${ix2.toFixed(2)},${iy2.toFixed(2)} A${r},${r} 0 ${large},0 ${ix1.toFixed(2)},${iy1.toFixed(2)} Z` };
  });
  const hovSlice = hov ? slices.find(s => s.key === hov) : null;
  return (
    <div style={{ position:'relative', display:'inline-block' }}>
      <svg width={DS} height={DS} viewBox={`0 0 ${DS} ${DS}`}>
        <circle cx={cx} cy={cy} r={R+3} fill="#0d0d10"/>
        {slices.map(s => (
          <path key={s.key} d={s.path} fill={s.color}
            opacity={hov && hov !== s.key ? 0.3 : 0.85}
            style={{ cursor:'pointer', transition:'opacity .12s' }}
            onMouseEnter={() => setHov(s.key)}
            onMouseLeave={() => setHov(null)}
            onClick={() => onSliceClick(s.key)}/>
        ))}
        <circle cx={cx} cy={cy} r={r-1} fill="#18181a" stroke="#1e3533" strokeWidth="1.5"/>
        <text x={cx} y={cy+8} textAnchor="middle"
          fontSize="21" fontFamily="'Black Ops One',sans-serif" fill="#f0ede6">{total}</text>
        <text x={cx} y={cy+18} textAnchor="middle"
          fontSize="5" fontFamily="'Share Tech Mono',monospace" fill="#5a5a5a" letterSpacing="1">ITEMS</text>
      </svg>
      {hovSlice && (
        <div style={{
          position:'absolute', bottom:'calc(50% + 36px)', left:'50%',
          transform:'translateX(-50%)',
          background:'#1a1a1f', border:`1px solid ${hovSlice.color}99`,
          borderRadius:4, padding:'.25rem .55rem',
          pointerEvents:'none', whiteSpace:'nowrap', zIndex:20,
          boxShadow:`0 4px 18px rgba(0,0,0,.8)`,
        }}>
          <div style={{ fontSize:'.6rem', fontFamily:"'Barlow Condensed',sans-serif",
            fontWeight:700, color:hovSlice.color }}>{hovSlice.label}</div>
          <div style={{ fontSize:'.5rem', color:'#7a7872', fontFamily:"'Share Tech Mono',monospace" }}>
            {hovSlice.count} item{hovSlice.count !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Gear Donut Chart ─────────────────────────────────────────────────────────
function GearDonut({ slices, total, totalVal, onSliceClick, activeFilter }) {
  const [hov, setHov] = useState(null);
  const DS = 152, R = 58, r = 36, cx = 76, cy = 76;
  const hovSlice = hov ? slices.find(s => s.key === hov) : null;
  return (
    <div style={{ background:'#18181a', border:'1px solid #1e3533', borderRadius:4, padding:'.7rem',
      display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'.3rem' }}>
      <div style={{ fontSize:'.5rem', color:'#5a5a5a', textTransform:'uppercase', letterSpacing:'.15em', alignSelf:'flex-start' }}>Item Mix</div>
      <div style={{ position:'relative' }}>
        <svg width={DS} height={DS} viewBox={`0 0 ${DS} ${DS}`}>
          <circle cx={cx} cy={cy} r={R+3} fill="#0d0d10"/>
          {slices.map(s => (
            <path key={s.key} d={s.path} fill={s.color}
              opacity={hov && hov !== s.key ? 0.3 : 0.85}
              style={{ cursor:'pointer', transition:'opacity .12s' }}
              onMouseEnter={() => setHov(s.key)}
              onMouseLeave={() => setHov(null)}
              onClick={() => onSliceClick(s.key)}/>
          ))}
          <circle cx={cx} cy={cy} r={r-1} fill="#18181a" stroke="#1e3533" strokeWidth="1.5"/>
          <text x={cx} y={cy+8} textAnchor="middle"
            fontSize="26" fontFamily="'Black Ops One',sans-serif" fill="#f0ede6">{total}</text>
          <text x={cx} y={cy+20} textAnchor="middle"
            fontSize="6" fontFamily="'Share Tech Mono',monospace" fill="#5a5a5a" letterSpacing="1">ITEMS</text>
        </svg>
        {/* Tooltip */}
        {hovSlice && (
          <div style={{
            position:'absolute', bottom:'calc(50% + 44px)', left:'50%',
            transform:'translateX(-50%)',
            background:'#1a1a1f', border:`1px solid ${hovSlice.color}99`,
            borderRadius:4, padding:'.28rem .65rem',
            pointerEvents:'none', whiteSpace:'nowrap', zIndex:20,
            boxShadow:`0 4px 18px rgba(0,0,0,.8), 0 0 10px ${hovSlice.color}22`,
          }}>
            <div style={{ fontSize:'.65rem', fontFamily:"'Barlow Condensed',sans-serif",
              fontWeight:700, letterSpacing:'.05em', color:hovSlice.color }}>{hovSlice.label}</div>
            <div style={{ fontSize:'.52rem', color:'#7a7872', fontFamily:"'Share Tech Mono',monospace" }}>
              {hovSlice.count} item{hovSlice.count !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
      <div style={{ textAlign:'center', lineHeight:1.4 }}>
        <div style={{ fontSize:'.95rem', fontFamily:"'Black Ops One',sans-serif", color:'#0eb8a0' }}>{'$'+fmt(totalVal)}</div>
        <div style={{ fontSize:'.45rem', color:'#5a5a5a', textTransform:'uppercase', letterSpacing:'.14em' }}>Est. Value</div>
      </div>
    </div>
  );
}

// ── Account Modal (Change Password + Locations) ────────────────────────────────
function AccountModal({ user, onClose, allTags, onFilterByTag }) {
  const isMob = useIsMobile();
  const [tab, setTab]               = useState('locations');
  const [locations, setLocations]   = useState([]);
  const [loadingLocs, setLoadingLocs] = useState(true);
  const [editingLoc, setEditingLoc] = useState(null);
  const [locForm, setLocForm]       = useState({ name:'', address:'', insurance_contact:'', notes:'', linked_tags:[] });
  const [savingLoc, setSavingLoc]   = useState(false);
  const [locErr, setLocErr]         = useState('');
  const [pw, setPw]                 = useState('');
  const [pw2, setPw2]               = useState('');
  const [savingPw, setSavingPw]     = useState(false);
  const [pwDone, setPwDone]         = useState(false);
  const [pwErr, setPwErr]           = useState('');

  useEffect(()=>{ loadLocations(); },[]);

  async function loadLocations() {
    setLoadingLocs(true);
    const { data, error } = await supabase.from('locations').select('*').eq('user_id', user.id).order('created_at');
    setLoadingLocs(false);
    if (!error) setLocations(data||[]);
  }

  function startNew() {
    setLocForm({ name:'', address:'', insurance_contact:'', notes:'', linked_tags:[] });
    setLocErr('');
    setEditingLoc('new');
  }

  function startEdit(loc) {
    setLocForm({ name:loc.name||'', address:loc.address||'', insurance_contact:loc.insurance_contact||'', notes:loc.notes||'', linked_tags:loc.linked_tags||[] });
    setLocErr('');
    setEditingLoc(loc.id);
  }

  async function saveLoc() {
    setLocErr('');
    if (!locForm.name.trim()) { setLocErr('Location name is required.'); return; }
    setSavingLoc(true);
    const payload = { user_id:user.id, name:locForm.name.trim(), address:locForm.address.trim(), insurance_contact:locForm.insurance_contact.trim(), notes:locForm.notes.trim(), linked_tags:locForm.linked_tags };
    const { error } = editingLoc === 'new'
      ? await supabase.from('locations').insert(payload)
      : await supabase.from('locations').update(payload).eq('id', editingLoc).eq('user_id', user.id);
    setSavingLoc(false);
    if (error) { setLocErr(error.message); return; }
    setEditingLoc(null);
    loadLocations();
  }

  async function deleteLoc(id) {
    if (!window.confirm('Delete this location?')) return;
    await supabase.from('locations').delete().eq('id', id).eq('user_id', user.id);
    loadLocations();
  }

  function toggleTag(tag) {
    setLocForm(f=>({ ...f, linked_tags: f.linked_tags.includes(tag) ? f.linked_tags.filter(t=>t!==tag) : [...f.linked_tags, tag] }));
  }

  async function handlePwSubmit() {
    setPwErr('');
    if (pw.length < 8) { setPwErr('Password must be at least 8 characters.'); return; }
    if (pw !== pw2)     { setPwErr('Passwords do not match.'); return; }
    setSavingPw(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    setSavingPw(false);
    if (error) { setPwErr(error.message); return; }
    setPwDone(true);
    setTimeout(()=>{ setPwDone(false); setPw(''); setPw2(''); }, 2500);
  }

  const inputSt = { background:'#0d0d0e', border:'1px solid #2e2e33', color:'#d4d2cc', borderRadius:4, padding:'.6rem .8rem', fontSize:'.8rem', fontFamily:"'Share Tech Mono',monospace", width:'100%', outline:'none', boxSizing:'border-box' };
  const wrapStyle = isMob
    ? { position:'fixed', inset:0, background:'rgba(0,0,0,.92)', zIndex:1100, display:'flex', flexDirection:'column', overflowY:'auto' }
    : { position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' };
  const boxStyle = isMob
    ? { background:'#18181a', flex:1, display:'flex', flexDirection:'column', paddingTop:'env(safe-area-inset-top,0px)' }
    : { background:'#18181a', border:'1px solid #2e2e33', borderRadius:8, width:'100%', maxWidth:560, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,.9)' };

  return (
    <div style={wrapStyle}>
      <div style={boxStyle}>
        {/* Header */}
        <div style={{ padding:isMob?'1rem 1rem .75rem':'1.25rem 1.4rem .75rem', borderBottom:'1px solid #2e2e33', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, paddingTop:isMob?'calc(env(safe-area-inset-top,0px) + 1rem)':'1.25rem' }}>
          <div>
            <div style={{ fontFamily:"'Black Ops One',sans-serif", fontSize:'1.05rem', color:'#f0ede6', letterSpacing:'.05em' }}>Account</div>
            <div style={{ fontSize:'.52rem', color:'#5a5a5a', letterSpacing:'.12em', textTransform:'uppercase', marginTop:'.12rem', maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.email}</div>
          </div>
          <Btn variant="ghost" onClick={onClose} style={{ fontSize:'1.1rem', minWidth:44, minHeight:44 }}>✕</Btn>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #2e2e33', flexShrink:0 }}>
          {[['locations','📍  Locations'],['password','🔑  Password']].map(([t,lbl])=>(
            <button key={t} onClick={()=>setTab(t)} style={{ flex:1, fontFamily:"'Share Tech Mono',monospace", fontSize:'.65rem', letterSpacing:'.1em', textTransform:'uppercase', padding:'.75rem', cursor:'pointer', border:'none', borderBottom:tab===t?`2px solid ${T.accent}`:'2px solid transparent', background:'transparent', color:tab===t?T.accent:'#5a5a5a', transition:'all .15s', marginBottom:-1 }}>{lbl}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:isMob?'1rem':'1.25rem 1.4rem' }}>

          {/* ── LOCATIONS TAB ── */}
          {tab==='locations' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
              <div style={{ fontSize:'.62rem', color:'#5a5a5a', lineHeight:1.7 }}>
                Link tags to physical locations. Use <span style={{ color:T.accent }}>View Gear</span> to instantly filter your vault by location — handy for insurance claims or moving.
              </div>

              {editingLoc && (
                <div style={{ background:'#111114', border:`1px solid ${T.accent}44`, borderRadius:6, padding:'1rem', display:'flex', flexDirection:'column', gap:'.75rem' }}>
                  <div style={{ fontFamily:"'Black Ops One',sans-serif", fontSize:'.85rem', color:'#f0ede6', letterSpacing:'.05em' }}>
                    {editingLoc==='new' ? 'New Location' : 'Edit Location'}
                  </div>
                  {[['name','Name *','e.g. Home, Practice Studio'],['address','Street Address','123 Main St, City, State ZIP'],['insurance_contact','Insurance Policy / Contact','Policy #, agent name, phone…']].map(([field,label,ph])=>(
                    <div key={field} style={{ display:'flex', flexDirection:'column', gap:'.28rem' }}>
                      <label style={{ fontSize:'.55rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.12em' }}>{label}</label>
                      <input value={locForm[field]} onChange={e=>setLocForm(f=>({...f,[field]:e.target.value}))} placeholder={ph} style={inputSt}/>
                    </div>
                  ))}
                  <div style={{ display:'flex', flexDirection:'column', gap:'.28rem' }}>
                    <label style={{ fontSize:'.55rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.12em' }}>Notes</label>
                    <textarea value={locForm.notes} onChange={e=>setLocForm(f=>({...f,notes:e.target.value}))} placeholder="Any extra notes…" rows={2} style={{...inputSt, resize:'vertical'}}/>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'.38rem' }}>
                    <label style={{ fontSize:'.55rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.12em' }}>Linked Tags</label>
                    {allTags.length===0
                      ? <div style={{ fontSize:'.6rem', color:'#3a3a3a', fontStyle:'italic' }}>No tags yet — tag your gear first.</div>
                      : <div style={{ display:'flex', flexWrap:'wrap', gap:'.35rem' }}>
                          {allTags.map(tag=>{
                            const active = locForm.linked_tags.includes(tag);
                            return <button key={tag} onClick={()=>toggleTag(tag)} style={{ fontSize:'.58rem', padding:'.22rem .55rem', borderRadius:10, cursor:'pointer', border:`1px solid ${active?T.accent2:'#2e2e33'}`, background:active?'rgba(155,109,255,.15)':'transparent', color:active?T.accent2:'#5a5a5a', fontFamily:'inherit', transition:'all .12s' }}>{tag}</button>;
                          })}
                        </div>
                    }
                  </div>
                  {locErr && <div style={{ fontSize:'.65rem', color:'#e05252', background:'rgba(224,82,82,.08)', border:'1px solid rgba(224,82,82,.2)', borderRadius:3, padding:'.45rem .7rem' }}>{locErr}</div>}
                  <div style={{ display:'flex', gap:'.5rem' }}>
                    <Btn onClick={()=>setEditingLoc(null)} disabled={savingLoc} style={{ flex:1 }}>Cancel</Btn>
                    <Btn variant="primary" onClick={saveLoc} disabled={savingLoc} style={{ flex:2 }}>
                      {savingLoc
                        ? <span style={{ display:'flex', alignItems:'center', gap:'.45rem' }}><span style={{ display:'inline-block', width:9, height:9, borderRadius:'50%', border:'2px solid rgba(0,0,0,.3)', borderTopColor:'#000', animation:'spin .7s linear infinite' }}/>Saving…</span>
                        : 'Save Location'}
                    </Btn>
                  </div>
                </div>
              )}

              {loadingLocs
                ? <div style={{ textAlign:'center', padding:'2rem', color:'#5a5a5a', fontSize:'.7rem', letterSpacing:'.1em' }}>Loading…</div>
                : locations.length===0 && !editingLoc
                  ? <div style={{ textAlign:'center', padding:'2rem 1rem', border:'1px dashed #2e2e33', borderRadius:6, color:'#3a3a3a', fontSize:'.65rem', letterSpacing:'.08em', lineHeight:1.8 }}>No locations yet.<br/>Add one to start tracking gear by location.</div>
                  : locations.map(loc=>(
                      <div key={loc.id} style={{ background:'#111114', border:'1px solid #2e2e33', borderRadius:6, overflow:'hidden' }}>
                        <div style={{ borderLeft:`3px solid ${T.accent}`, padding:'.75rem 1rem', display:'flex', flexDirection:'column', gap:'.4rem' }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'.5rem' }}>
                            <div style={{ fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, fontSize:'.95rem', color:'#f0ede6' }}>{loc.name}</div>
                            <div style={{ display:'flex', gap:'.3rem', flexShrink:0 }}>
                              <Btn size="xs" variant="teal" onClick={()=>{ onFilterByTag(loc.linked_tags); onClose(); }} style={{ fontSize:'.55rem', minHeight:28, padding:'.15rem .5rem' }}>View Gear</Btn>
                              <Btn size="xs" variant="silver" onClick={()=>startEdit(loc)} style={{ minHeight:28, padding:'.15rem .45rem' }}>Edit</Btn>
                              <Btn size="xs" variant="danger" onClick={()=>deleteLoc(loc.id)} style={{ minHeight:28, padding:'.15rem .4rem' }}>✕</Btn>
                            </div>
                          </div>
                          {loc.address && <div style={{ fontSize:'.6rem', color:'#7a7872' }}>📍 {loc.address}</div>}
                          {loc.insurance_contact && <div style={{ fontSize:'.6rem', color:'#7a7872' }}>🛡 {loc.insurance_contact}</div>}
                          {loc.notes && <div style={{ fontSize:'.6rem', color:'#5a5a5a', fontStyle:'italic' }}>{loc.notes}</div>}
                          {(loc.linked_tags||[]).length>0 && (
                            <div style={{ display:'flex', gap:'.3rem', flexWrap:'wrap', marginTop:'.1rem' }}>
                              {(loc.linked_tags||[]).map(tag=>(
                                <span key={tag} style={{ fontSize:'.5rem', padding:'.1rem .38rem', borderRadius:8, background:'rgba(155,109,255,.1)', border:'1px solid rgba(155,109,255,.2)', color:T.accent2 }}>{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
              }

              {!editingLoc && (
                <Btn variant="primary" onClick={startNew} style={{ alignSelf:'flex-start', minHeight:38 }}>+ Add Location</Btn>
              )}
            </div>
          )}

          {/* ── PASSWORD TAB ── */}
          {tab==='password' && (
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem', maxWidth:380 }}>
              {pwDone ? (
                <div style={{ textAlign:'center', padding:'2rem 0' }}>
                  <div style={{ fontSize:'2.2rem', marginBottom:'.5rem' }}>✓</div>
                  <div style={{ color:T.accent, fontSize:'.82rem', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'.06em' }}>Password updated!</div>
                </div>
              ) : (<>
                <div style={{ display:'flex', flexDirection:'column', gap:'.3rem' }}>
                  <label style={{ fontSize:'.58rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.14em' }}>New Password</label>
                  <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="Min. 8 characters" style={inputSt} onKeyDown={e=>e.key==='Enter'&&handlePwSubmit()}/>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'.3rem' }}>
                  <label style={{ fontSize:'.58rem', color:'#7a7872', textTransform:'uppercase', letterSpacing:'.14em' }}>Confirm New Password</label>
                  <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} placeholder="Repeat password" style={inputSt} onKeyDown={e=>e.key==='Enter'&&handlePwSubmit()}/>
                </div>
                {pwErr && <div style={{ fontSize:'.68rem', color:'#e05252', background:'rgba(224,82,82,.08)', border:'1px solid rgba(224,82,82,.2)', borderRadius:3, padding:'.5rem .75rem' }}>{pwErr}</div>}
                <Btn variant="primary" onClick={handlePwSubmit} disabled={savingPw} style={{ alignSelf:'flex-start', minHeight:42, paddingLeft:'1.4rem', paddingRight:'1.4rem' }}>
                  {savingPw
                    ? <span style={{ display:'flex', alignItems:'center', gap:'.5rem' }}><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', border:'2px solid rgba(0,0,0,.3)', borderTopColor:'#000', animation:'spin .7s linear infinite' }}/>Updating…</span>
                    : 'Update Password'}
                </Btn>
              </>)}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

// ── Info Modal ───────────────────────────────────────────────────────────────
function InfoModal({ onClose }) {
  const isMob = useIsMobile();
  const [showPrivacy, setShowPrivacy] = useState(false);
  const containerStyle = isMob
    ? { position:'fixed', inset:0, background:'#18181a', display:'flex', flexDirection:'column', zIndex:1001, overflowY:'auto', paddingBottom:'env(safe-area-inset-bottom,1.5rem)' }
    : { background:'#18181a', border:'1px solid #2e2e33', borderRadius:4, width:'100%', maxWidth:560, maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.9)' };
  const wrapStyle = isMob
    ? { position:'fixed', inset:0, zIndex:1000 }
    : { position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' };

  const S = {
    h2: { fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1rem', color:'#f0ede6', marginBottom:'.5rem', marginTop:'1.25rem' },
    p:  { fontSize:'.78rem', color:'#9b9b99', lineHeight:1.75, marginBottom:'.5rem' },
    li: { fontSize:'.78rem', color:'#9b9b99', lineHeight:1.75, paddingLeft:'.25rem' },
    accent: { color:'#0eb8a0' },
    accent2: { color:'#9b6dff' },
    divider: { height:1, background:'#2e2e33', margin:'1rem 0' },
    tag: { display:'inline-block', fontSize:'.6rem', padding:'.18rem .55rem', borderRadius:10,
           background:'rgba(14,184,160,.12)', border:'1px solid rgba(14,184,160,.25)',
           color:'#0eb8a0', marginRight:'.35rem', marginBottom:'.35rem' },
  };

  return (
    <div style={wrapStyle} onClick={isMob ? undefined : e=>{ if(e.target===e.currentTarget) onClose(); }}>
      <div style={containerStyle}>
        {/* Header */}
        <div style={{ padding:isMob?'1rem':'1.1rem 1.4rem', borderBottom:'1px solid #2e2e33', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#1f1f22', position:'sticky', top:0, paddingTop:isMob?'calc(env(safe-area-inset-top,0px) + 1rem)':'1.1rem' }}>
          <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.15rem', color:'#f0ede6' }}>
            About <span style={S.accent}>GearVault</span>
          </div>
          <Btn variant="ghost" onClick={onClose} style={{ fontSize:'1.1rem', minWidth:44, minHeight:44 }}>✕</Btn>
        </div>

        <div style={{ padding:isMob?'1.1rem 1rem':'1.35rem 1.4rem', display:'flex', flexDirection:'column' }}>

          {/* What is it */}
          <p style={S.p}>
            <span style={S.accent}>GearVault</span> is a free, private inventory manager built for musicians.
            Track everything you own — guitars, basses, amps, pedals, keys, studio gear and more —
            with photos, market values, serial decoders, and location tracking for insurance purposes.
          </p>
          <p style={S.p}>
            Your vault is <span style={{ color:'#f0ede6' }}>yours only</span>. Each account is private — nothing is shared.
          </p>

          <div style={S.divider}/>

          {/* Getting Started */}
          <div style={S.h2}>Getting Started</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'.65rem' }}>
            {[
              ['➕  Add Gear', 'Tap + Add Gear (or the floating button on mobile). Fill in make, model, category, and condition. Everything else is optional. The form stays open until your gear is saved — just wait for the confirmation.'],
              ['📸  Photos', 'Add up to 2 photos per item. Tap any photo to open the full-screen lightbox viewer. Both photos appear on the share card.'],
              ['↺  Reverb Lookup', 'Hit the ↺ button on any card to pull a live median market value from Reverb.com listings. Set a custom search term in the edit form if the auto-query pulls wrong results.'],
              ['📅  Serial Date Decoder', 'Enter a serial number in the edit form and hit "Date It" to decode the manufacture year. Supports Fender, Gibson, Martin, Taylor, PRS, Ibanez, and Charvel.'],
              ['🏷  Labels & Tags', 'Tag gear with custom labels like "Touring Rig", "Studio", or "For Sale". Use the By Label toggle to group your vault by tag. Labels are reused via autocomplete as you type.'],
              ['🔍  Search & Filter', 'The search bar (below the filter strip) finds by make, model, serial, notes, or tag. Category pills filter the grid. Sort by Make, Model, Value, Year, or Date Added.'],
              ['⊞ / ☰  Grid & List View', 'Toggle between a photo grid and a compact list view using the ⊞ / ☰ buttons. Your preference is saved and restored next time you log in.'],
              ['📦  Export Vault', 'Download a CSV of your full vault, or filtered by category or label — useful for insurance documentation or spreadsheet tracking.'],
              ['⬆  Share as Image', 'On mobile, open any item and tap the share icon to generate a portrait photo card you can send via your phone\'s share sheet.'],
            ].map(([title, desc]) => (
              <div key={title} style={{ background:'#111114', border:'1px solid #2a2a2e', borderRadius:4, padding:'.7rem .9rem' }}>
                <div style={{ fontSize:'.7rem', color:'#f0ede6', fontWeight:600, marginBottom:'.25rem', letterSpacing:'.04em' }}>{title}</div>
                <div style={{ fontSize:'.72rem', color:'#7a7872', lineHeight:1.6 }}>{desc}</div>
              </div>
            ))}
          </div>

          <div style={S.divider}/>

          {/* Locations */}
          <div style={S.h2}>📍 Locations & Insurance</div>
          <p style={S.p}>
            Create named locations — Home, Practice Studio, Storage Unit — each with a street address, insurance policy or contact, and notes.
            Link one or more of your Labels to each location, then hit <span style={S.accent}>View Gear</span> on the location card to instantly filter your vault to everything at that address.
          </p>
          <p style={S.p}>
            If a location is ever affected by theft, fire, or damage, you can pull the full inventory in seconds and share the export with your insurer.
            Manage your locations under <span style={{ color:'#f0ede6' }}>⏻ → Account → Locations</span>.
          </p>

          <div style={S.divider}/>

          {/* Categories */}
          <div style={S.h2}>Categories</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'.4rem', marginBottom:'.5rem' }}>
            {Object.values(CATS).map(c=>(
              <span key={c.label} style={{ ...S.tag, background:`${c.color}18`, border:`1px solid ${c.color}44`, color:c.color }}>{c.label}</span>
            ))}
          </div>
          <p style={S.p}>
            <span style={S.accent}>Pedals & Effects</span> — includes a sub-type field (Overdrive, Fuzz, Reverb, Delay, etc. across 8 groups).<br/>
            <span style={S.accent}>Guitars & Bass</span> — include primary tuning (40+ options) and string gauge fields.<br/>
            <span style={S.accent}>Live Sound</span> — includes an equipment type field (Microphones, Monitors, Wireless, Mixers, etc.).<br/>
            <span style={S.accent}>Orchestra</span> — select your section (Strings, Woodwinds, Brass, Percussion) then pick your specific instrument from that section.
          </p>

          <div style={S.divider}/>

          {/* Account */}
          <div style={S.h2}>🔑 Account</div>
          <p style={S.p}>
            Access your account settings via the <span style={{ color:'#f0ede6' }}>⏻ power icon</span> in the top-right corner.
            From there you can open Account (locations + password) or log out.
            You can change your password at any time without needing a reset email — just go to Account → Password tab.
          </p>

          <div style={S.divider}/>

          {/* Tips */}
          <div style={S.h2}>Tips</div>
          {[
            'Add GearVault to your home screen for a full-screen app experience on iOS and Android.',
            'Use Labels strategically — one label per physical location makes the insurance filter instant.',
            'The Manual Value field lets you set your own appraisal alongside the Reverb estimate — useful for vintage or rare gear Reverb doesn\'t list often.',
            'Sort by Value ↓ to quickly see your most expensive items for insurance prioritisation.',
            'The Gear Breakdown charts at the top are clickable — tap any category to filter the grid instantly.',
          ].map((tip, i) => (
            <p key={i} style={{ ...S.p, marginBottom:'.3rem' }}>• {tip}</p>
          ))}

          <div style={S.divider}/>

          {/* Support */}
          <div style={{ textAlign:'center', padding:'.5rem 0 .25rem' }}>
            <div style={{ fontSize:'.65rem', color:'#7a7872', marginBottom:'.65rem', lineHeight:1.6 }}>
              GearVault is free to use, forever.<br/>
              If it's saved you time or helped protect your collection,<br/>
              a coffee goes a long way. ☕
            </div>
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" style={{
              display:'inline-flex', alignItems:'center', gap:'.5rem',
              background:'#1f1a10', border:'1px solid #5a3e00',
              color:'#f5a623', borderRadius:24,
              padding:'.55rem 1.3rem', marginBottom:'.25rem',
              fontSize:'.72rem', fontFamily:"'Share Tech Mono',monospace",
              letterSpacing:'.08em', textDecoration:'none', textTransform:'uppercase',
              boxShadow:'0 2px 16px rgba(245,166,35,.15)',
              transition:'all .15s',
            }}
              onMouseOver={e=>{ e.currentTarget.style.background='#2a2010'; e.currentTarget.style.boxShadow='0 4px 20px rgba(245,166,35,.3)'; }}
              onMouseOut={e=>{ e.currentTarget.style.background='#1f1a10'; e.currentTarget.style.boxShadow='0 2px 16px rgba(245,166,35,.15)'; }}
            >
              <span style={{ fontSize:'1rem' }}>☕</span> Buy Me a Coffee
            </a>
          </div>

          <div style={S.divider}/>
          <SiteFooter onPrivacy={()=>setShowPrivacy(true)}/>
        </div>
      </div>
      {showPrivacy && <PrivacyModal onClose={()=>setShowPrivacy(false)}/>}
    </div>
  );
}

// ── Export Dropdown Menu ──────────────────────────────────────────────────────
function ExportMenu({ gear, allTags, onExport, onClose, isMob }) {
  const ref = useRef(null);
  // Close on outside click
  useEffect(()=>{
    function handler(e){ if(ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return ()=>{ document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  },[onClose]);

  const menuStyle = {
    position:'absolute', top:'calc(100% + 6px)', right:0,
    background:'#1a1a1c', border:'1px solid #2e2e33',
    borderRadius:6, boxShadow:'0 8px 32px rgba(0,0,0,.7)',
    zIndex:2000, minWidth:220, overflow:'hidden',
  };
  const sectionStyle = {
    padding:'.35rem .6rem .2rem',
    fontSize:'.52rem', color:'#5a5a5a',
    textTransform:'uppercase', letterSpacing:'.14em',
    borderTop:'1px solid #2e2e33',
  };
  const itemStyle = (color='#d4d2cc') => ({
    display:'block', width:'100%', textAlign:'left',
    background:'transparent', border:'none',
    padding:'.55rem .9rem', cursor:'pointer',
    fontFamily:'inherit', fontSize:'.72rem',
    color, letterSpacing:'.04em',
    WebkitTapHighlightColor:'transparent',
    transition:'background .1s',
  });

  const cats = Object.keys(CATS).filter(k=>gear.some(g=>g.cat===k));

  return (
    <div ref={ref} style={menuStyle}>
      {/* All */}
      <div style={{ padding:'.3rem 0' }}>
        <button style={itemStyle('#0eb8a0')}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(14,184,160,.1)'}
          onMouseLeave={e=>e.currentTarget.style.background='transparent'}
          onClick={()=>{ onExport(gear,'All_Gear'); onClose(); }}>
          📦 Export All Gear <span style={{ fontSize:'.6rem', color:'#5a5a5a' }}>({gear.length} items)</span>
        </button>
      </div>
      {/* By Category */}
      {cats.length > 0 && <>
        <div style={{ ...sectionStyle, borderTop:'1px solid #2e2e33' }}>By Category</div>
        <div style={{ padding:'.2rem 0' }}>
          {cats.map(k=>{
            const items = gear.filter(g=>g.cat===k);
            const col = CATS[k]?.color||'#7a7872';
            return (
              <button key={k} style={itemStyle(col)}
                onMouseEnter={e=>e.currentTarget.style.background=col+'18'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                onClick={()=>{ onExport(items, CATS[k]?.label||k); onClose(); }}>
                <CatIcon cat={k} size={12} color={col} style={{display:'inline-block',verticalAlign:'middle',marginRight:6}}/>
                {CATS[k]?.label||k} <span style={{ fontSize:'.6rem', color:'#5a5a5a' }}>({items.length})</span>
              </button>
            );
          })}
        </div>
      </>}
      {/* By Tag */}
      {allTags.length > 0 && <>
        <div style={{ ...sectionStyle }}>By Label</div>
        <div style={{ padding:'.2rem 0', maxHeight:180, overflowY:'auto' }}>
          {allTags.map(tag=>{
            const items = gear.filter(g=>(g.tags||[]).includes(tag));
            return (
              <button key={tag} style={itemStyle('#9b6dff')}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(155,109,255,.12)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                onClick={()=>{ onExport(items, tag); onClose(); }}>
                🏷 {tag} <span style={{ fontSize:'.6rem', color:'#5a5a5a' }}>({items.length})</span>
              </button>
            );
          })}
        </div>
      </>}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function GearVault({ user, onSignOut }) {
  const [gear, setGear]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [editId, setEditId]   = useState(null);
  const [viewId, setViewId]   = useState(null);
  const [filter, setFilter]   = useState('All');
  const [tagFilter, setTagFilter] = useState(null);
  // Load persisted view prefs
  const _prefs = (() => { try { return JSON.parse(localStorage.getItem(PREFS_CACHE_KEY)||'{}'); } catch { return {}; } })();
  const [groupBy,  setGroupBy]  = useState(_prefs.groupBy  || 'type');
  const [sortBy,   setSortBy]   = useState(_prefs.sortBy   || 'make');
  const [listView, setListView] = useState(_prefs.listView || false);
  const [search, setSearch]   = useState('');
  const [saving, setSaving]   = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [toasts, setToasts]   = useState([]);
  const [showPrivacyMain, setShowPrivacyMain] = useState(false);
  const [showAccount,     setShowAccount]     = useState(false);
  const [userMenuOpen,    setUserMenuOpen]    = useState(false);

  // Close user dropdown on outside click
  useEffect(()=>{
    if (!userMenuOpen) return;
    const handler = ()=>setUserMenuOpen(false);
    setTimeout(()=>window.addEventListener('click', handler), 0);
    return ()=>window.removeEventListener('click', handler);
  },[userMenuOpen]);
  const isMob = useIsMobile();

  // All unique tags across all gear items, persisted in localStorage
  const allTags = useMemo(()=>{
    const set = new Set();
    gear.forEach(g=>(g.tags||[]).forEach(t=>set.add(t)));
    try { localStorage.setItem(TAGS_CACHE_KEY, JSON.stringify([...set])); } catch {}
    return [...set].sort();
  },[gear]);

  function toast(msg, type='') {
    const id=uid();
    setToasts(t=>[...t,{id,msg,type}]);
    setTimeout(()=>setToasts(t=>t.filter(x=>x.id!==id)),3200);
  }

  // ── Load — show cache instantly, then refresh in background ─────────────
  const loadGear = useCallback(async (silent=false) => {
    if (!silent) {
      try {
        const cached = localStorage.getItem(VAULT_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed.userId === user.id && Array.isArray(parsed.gear)) {
            setGear(parsed.gear);
          }
        }
      } catch {}
    }
    setLoading(true);
    try {
      let { data, error } = await supabase
        .from('gear')
        .select('id,cat,cond,make,model,yr,sn,paid,mv,rv,rvc,pend,notes,tuning,gauge,reverb_query,tags,created_at')
        .order('cat').order('created_at',{ascending:false});

      // If new columns (tags/reverb_query) don't exist yet, fall back to select *
      if (error && (error.code==='42703' || (error.message||'').includes('column'))) {
        const fallback = await supabase.from('gear').select('id,cat,cond,make,model,yr,sn,paid,mv,rv,rvc,pend,notes,tuning,gauge,created_at')
          .order('cat').order('created_at',{ascending:false});
        data  = fallback.data;
        error = fallback.error;
        if (!error) toast('Run the SQL migration to enable tags & Reverb search','inf');
      }

      if (error) {
        toast('Failed to load gear: '+error.message,'err');
      } else {
        const rows = (data||[]).map(g=>({ ...g, tags: g.tags||[], imgs: [] })); // imgs loaded lazily per-item
        setGear(rows);
        try {
          localStorage.setItem(VAULT_CACHE_KEY, JSON.stringify({ userId: user.id, gear: rows, ts: Date.now() }));
        } catch {}
        // Show About page on first-ever login (flag persisted in localStorage)
        try {
          if (!localStorage.getItem(ONBOARDED_KEY)) {
            setModal('info');
            localStorage.setItem(ONBOARDED_KEY, '1');
          }
        } catch {}
      }
    } catch(e) {
      toast('Load error: '+e.message,'err');
    } finally {
      setLoading(false);
    }
  },[user.id]);

  useEffect(()=>{ loadGear(); },[loadGear]);

  // ── Progressive image loader — fires once when the metadata list lands ─────
  // Deps: only [loading] — we snapshot IDs into a local variable so setGear
  // calls inside the loop never re-trigger this effect.
  useEffect(()=>{
    if (loading) return;           // wait for metadata load to finish
    // Snapshot IDs at the moment loading completes — never reads gear again
    const ids = gear.filter(g=>!g._imgsLoaded).map(g=>g.id);
    if (ids.length===0) return;
    let cancelled = false;
    (async () => {
      for (const id of ids) {
        if (cancelled) break;
        try {
          const { data } = await supabase
            .from('gear').select('id,imgs,img').eq('id',id).single();
          if (cancelled || !data) continue;
          const imgs = data.imgs||(data.img?[data.img]:[]);
          setGear(g=>g.map(x=>x.id===id?{...x,imgs,_imgsLoaded:true}:x));
        } catch {}
        await new Promise(r=>setTimeout(r,80));
      }
    })();
    return () => { cancelled = true; };
  },[loading]); // ← only [loading], not gear — prevents re-run on each image swap


  // ── Lazy-load images for a single item (called when opening view/edit) ────
  const loadItemImgs = useCallback(async (id) => {
    // Already loaded?
    const existing = gear.find(x=>x.id===id);
    if (existing?._imgsLoaded) return existing;
    const { data, error } = await supabase
      .from('gear').select('id,imgs,img').eq('id',id).single();
    if (error || !data) return existing;
    const imgs = data.imgs||(data.img?[data.img]:[]);
    setGear(g=>g.map(x=>x.id===id?{...x,imgs,_imgsLoaded:true}:x));
    return { ...existing, imgs, _imgsLoaded:true };
  },[gear]);

  // ── Save with optimistic update — NO full reload ─────────────────────────
  async function saveGear(form) {
    setSaving(true);
    const imgs=form.imgs||(form.img?[form.img]:[]);
    const payload={
      user_id:user.id, cat:form.cat, cond:form.cond,
      make:form.make.trim(), model:form.model.trim(),
      yr:form.yr.trim(), sn:form.sn.trim(),
      paid:form.paid?parseFloat(form.paid):null,
      mv:form.mv?parseFloat(form.mv):null,
      notes:form.notes.trim(), imgs, img:imgs[0]||null,
      pedal_type: form.cat==='Pedals'?(form.pedal_type||null):null,
      live_type:      form.cat==='LiveSound'?(form.live_type||null):null,
      orch_section:   form.cat==='Orchestra'?(form.orch_section||null):null,
      orch_instrument:form.cat==='Orchestra'?(form.orch_instrument||null):null,
      tuning:(form.cat==='Guitars'||form.cat==='Bass')?(form.tuning||null):null,
      gauge: (form.cat==='Guitars'||form.cat==='Bass')?(form.gauge||null):null,
      reverb_query: form.reverb_query?.trim()||null,
      tags: form.tags||[],
      rv:null, rvc:0, pend:false,
    };
    if(editId) {
      // Optimistic: update in-place immediately
      const updated = gear.map(x=>x.id===editId?{...x,...payload,id:editId}:x);
      setGear(updated);
      try { localStorage.setItem(VAULT_CACHE_KEY, JSON.stringify({ userId:user.id, gear:updated, ts:Date.now() })); } catch {}
      setModal(null); setEditId(null); setSaving(false);
      const { error } = await supabase.from('gear').update(payload).eq('id',editId).eq('user_id',user.id);
      if(error){ toast('Save failed: '+error.message,'err'); loadGear(); return; }
      toast('Gear updated!','ok');
    } else {
      // Keep modal open + show saving state until insert completes
      const { data, error } = await supabase.from('gear').insert(payload).select().single();
      setSaving(false);
      if(error){ toast('Save failed: '+error.message,'err'); return; }
      setModal(null);
      // Prepend new item
      setGear(g=>{
        const updated=[data,...g];
        try { localStorage.setItem(VAULT_CACHE_KEY, JSON.stringify({ userId:user.id, gear:updated, ts:Date.now() })); } catch {}
        return updated;
      });
      toast('Gear added!','ok');
    }
  }

  // ── Delete with optimistic update ────────────────────────────────────────
  async function deleteItem(id) {
    const item=gear.find(x=>x.id===id);
    if(!item||!confirm(`Remove "${item.make} ${item.model}"?`)) return;
    setGear(g=>{
      const updated=g.filter(x=>x.id!==id);
      try { localStorage.setItem(VAULT_CACHE_KEY, JSON.stringify({ userId:user.id, gear:updated, ts:Date.now() })); } catch {}
      return updated;
    }); // remove immediately
    const { error }=await supabase.from('gear').delete().eq('id',id).eq('user_id',user.id);
    if(error){ toast('Delete failed: '+error.message,'err'); loadGear(); return; }
    toast('Item removed.');
  }

  // ── Reverb lookup from the form (updates form.rv live) ──────────────────────
  async function doReverbFromForm(form, setFormRv) {
    const rawQuery = form.reverb_query?.trim() ||
      [form.make, form.model, form.yr].filter(Boolean).join(' ').trim();
    if (!rawQuery) { toast('Enter Make + Model first','err'); return; }
    const query = encodeURIComponent(rawQuery);
    try {
      const res = await fetch(
        `https://api.gearvault.xyz/api/reverb/search?q=${query}`,
        { headers:{ 'Accept':'application/hal+json','Accept-Version':'3.0',
          'Authorization':`Bearer ${localStorage.getItem('gearvault-auth-token')}` } }
      );
      if(!res.ok) throw new Error('HTTP '+res.status);
      const data = await res.json();
      const prices=(data.listings||[]).map(l=>parseFloat(l.price?.amount||0)).filter(p=>p>10);
      if(!prices.length){ toast('No Reverb listings found — try different search terms','inf'); return; }
      prices.sort((a,b)=>a-b);
      const mid=Math.floor(prices.length/2);
      const median=prices.length%2===0?(prices[mid-1]+prices[mid])/2:prices[mid];
      setFormRv(median.toFixed(2));
      toast(`Reverb: $${fmt(median)} median · ${prices.length} listings`,'ok');
    } catch {
      const searchUrl=`https://reverb.com/marketplace?query=${query}`;
      toast(`CORS blocked — check reverb.com manually`,'inf');
    }
  }

  // ── Reverb lookup (non-blocking, updates in-place) ────────────────────────
  async function doReverb(id, item) {
    setGear(g=>g.map(x=>x.id===id?{...x,pend:true}:x));
    // Use the manual override if set, otherwise build from fields
    const rawQuery = item.reverb_query?.trim() ||
      [item.make, item.model, item.yr].filter(Boolean).join(' ').trim();
    const query = encodeURIComponent(rawQuery);
    try {
      const res = await fetch(
        `https://api.gearvault.xyz/api/reverb/search?q=${query}`,
        { headers:{ 'Accept':'application/hal+json', 'Accept-Version':'3.0',
          'Authorization':`Bearer ${localStorage.getItem('gearvault-auth-token')}` } }
      );
      if(!res.ok) throw new Error('HTTP '+res.status);
      const data = await res.json();
      const prices = (data.listings||[])
        .map(l=>parseFloat(l.price?.amount||0))
        .filter(p=>p>10);
      if(!prices.length) throw new Error('no listings');
      // Median instead of mean — more resilient to outliers
      prices.sort((a,b)=>a-b);
      const mid = Math.floor(prices.length/2);
      const median = prices.length%2===0 ? (prices[mid-1]+prices[mid])/2 : prices[mid];
      await supabase.from('gear').update({rv:median.toFixed(2),rvc:prices.length,pend:false}).eq('id',id).eq('user_id',user.id);
      setGear(g=>g.map(x=>x.id===id?{...x,rv:median.toFixed(2),rvc:prices.length,pend:false}:x));
      toast(`Reverb: $${fmt(median)} median · ${prices.length} listings`,'ok');
    } catch {
      await supabase.from('gear').update({pend:false}).eq('id',id).eq('user_id',user.id);
      setGear(g=>g.map(x=>x.id===id?{...x,pend:false}:x));
      const searchUrl = `https://reverb.com/marketplace?query=${query}`;
      toast(`Reverb CORS blocked — set a manual value, or check: ${searchUrl}`,'inf');
    }
  }

  async function doReverbAll() {
    const items = gear.filter(g => g.make && g.model);
    if (!items.length) { toast('No gear to refresh', 'err'); return; }
    toast(`Refreshing ${items.length} items from Reverb…`, 'inf');
    let updated = 0, failed = 0;
    for (const item of items) {
      const rawQuery = item.reverb_query?.trim() ||
        [item.make, item.model, item.yr].filter(Boolean).join(' ').trim();
      const query = encodeURIComponent(rawQuery);
      try {
        const res = await fetch(
          `https://api.gearvault.xyz/api/reverb/search?q=${query}`,
          { headers: { 'Accept': 'application/hal+json', 'Accept-Version': '3.0',
            'Authorization': `Bearer ${localStorage.getItem('gearvault-auth-token')}` } }
        );
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const prices = (data.listings || [])
          .map(l => parseFloat(l.price?.amount || 0))
          .filter(p => p > 10);
        if (!prices.length) { failed++; continue; }
        prices.sort((a, b) => a - b);
        const mid = Math.floor(prices.length / 2);
        const median = prices.length % 2 === 0 ? (prices[mid - 1] + prices[mid]) / 2 : prices[mid];
        await supabase.from('gear').update({ rv: median.toFixed(2), rvc: prices.length, pend: false }).eq('id', item.id).eq('user_id', user.id);
        setGear(g => g.map(x => x.id === item.id ? { ...x, rv: median.toFixed(2), rvc: prices.length, pend: false } : x));
        updated++;
        // Small delay to avoid hammering the API
        await new Promise(r => setTimeout(r, 300));
      } catch {
        failed++;
      }
    }
    toast(`Reverb refresh complete — ${updated} updated, ${failed} not found`, updated > 0 ? 'ok' : 'err');
  }

  function exportItems(items, label) {
    if(!items.length){ toast('Nothing to export','err'); return; }
    const H=['#','Category','Make','Model','Year','Serial','Condition','Paid','Reverb Est.','Manual Value','Tags','Notes','Photos'];
    const rows=items.map((g,i)=>{ const imgs=g.imgs||(g.img?[g.img]:[]); return [i+1,CATS[g.cat]?.label||g.cat,g.make,g.model,g.yr||'',g.sn||'',g.cond||'',g.paid||'',g.rv||'',g.mv||'',(g.tags||[]).join('; '),g.notes||'',imgs.length]; });
    const csv=[H,...rows].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const slug=label.replace(/[^a-z0-9]/gi,'_');
    const a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download=`GearVault_${slug}_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    toast(`📦 Exported: ${label}`,'ok');
  }



  // ── Derived state ──────────────────────────────────────────────────────────
  const filtered = useMemo(()=>{
    const f = gear.filter(g=>{
      const cm=filter==='All'||g.cat===filter;
      const q=search.toLowerCase();
      const qm=!q||[g.make,g.model,g.sn,g.notes,g.cat,...(g.tags||[])].join(' ').toLowerCase().includes(q);
      const tm=!tagFilter||(g.tags||[]).includes(tagFilter);
      return cm&&qm&&tm;
    });
    return f.sort((a,b)=>{
      if(sortBy==='make')  return (a.make||'').localeCompare(b.make||'');
      if(sortBy==='model') return (a.model||'').localeCompare(b.model||'');
      if(sortBy==='value') return (parseFloat(b.rv||b.mv)||0)-(parseFloat(a.rv||a.mv)||0);
      if(sortBy==='year')  return (parseInt(b.yr)||0)-(parseInt(a.yr)||0);
      if(sortBy==='added') return new Date(b.created_at||0)-new Date(a.created_at||0);
      return 0;
    });
  },[gear,filter,tagFilter,search,sortBy]);

  // Group by type (category) or by tag
  const grouped = useMemo(()=>{
    if (groupBy === 'tag') {
      // Group by tag; items with no tags go into 'Untagged'
      const map = {};
      filtered.forEach(g=>{
        const tags = g.tags||[];
        if (tags.length===0) {
          if (!map['__none__']) map['__none__']=[];
          map['__none__'].push(g);
        } else {
          tags.forEach(tag=>{
            if (!map[tag]) map[tag]=[];
            map[tag].push(g);
          });
        }
      });
      const tagKeys = Object.keys(map).filter(k=>k!=='__none__').sort();
      const all = [...tagKeys, ...('__none__' in map ? ['__none__'] : [])];
      return all.map(k=>({ key:k, label:k==='__none__'?'Untagged':k, color:T.accent2, items:map[k] }));
    } else {
      const order=Object.keys(CATS);
      const map={};
      filtered.forEach(g=>{ if(!map[g.cat]) map[g.cat]=[]; map[g.cat].push(g); });
      return order.filter(k=>map[k]?.length).map(k=>({
        key:k, label:CATS[k]?.label||k, color:CATS[k]?.color||'#7a7872', items:map[k]
      }));
    }
  },[filtered, groupBy]);

  const totalVal  = useMemo(()=>gear.reduce((s,g)=>s+(parseFloat(g.rv||g.mv)||0),0),[gear]);
  const viewItem  = gear.find(x=>x.id===viewId);
  const editItem  = gear.find(x=>x.id===editId);

  return (
    <div style={{ background:'#111112', minHeight:'100vh', fontFamily:"'Share Tech Mono','Courier New',monospace", color:'#d4d2cc', paddingBottom:isMob?'80px':'0' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Black+Ops+One&family=Share+Tech+Mono&family=Barlow+Condensed:wght@300;400;600;700&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        select option{background:#0d0d0e;color:#d4d2cc}
        @keyframes spin{to{transform:rotate(360deg)}}
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:#0d0d0e} ::-webkit-scrollbar-thumb{background:#2e2e33;border-radius:2px}
        input,select,textarea{-webkit-appearance:none;appearance:none}
      `}</style>

      {/* ── Header ── */}
      <header style={{ background:'#18181a', borderBottom:`2px solid #1e3533`, position:'sticky', top:0, zIndex:500, boxShadow:'0 4px 20px rgba(0,0,0,.6)', paddingTop:'env(safe-area-inset-top,0px)' }}>
        <div style={{ maxWidth:1440, margin:'0 auto', padding:'0 1rem', height:56, display:'flex', alignItems:'center', gap:'1rem' }}>
          <div onClick={()=>{ setModal(null); setViewId(null); setEditId(null); setSearch(''); setTagFilter(null); }}
            style={{ display:'flex', alignItems:'center', gap:'1rem', cursor:'pointer', WebkitTapHighlightColor:'transparent' }}>
            <PickLogo size={32} />
            <div>
              <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.2rem', letterSpacing:'.06em', color:'#f0ede6', lineHeight:1 }}>Gear<span style={{ color:T.accent }}>Vault</span></div>
              {!isMob && <div style={{ fontSize:'.5rem', color:'#7a7872', letterSpacing:'.2em', textTransform:'uppercase' }}>Musician Inventory</div>}
            </div>
          </div>
          <div style={{ flex:1 }} />
          {isMob ? (
            <div style={{ display:'flex', gap:'.4rem', alignItems:'center' }}>
              <div style={{ position:'relative' }}>
                <button onClick={()=>setExportOpen(v=>!v)} style={{ background:'transparent', border:'none', color:'#7a7872', fontSize:'.9rem', padding:'.4rem', cursor:'pointer', minHeight:36, minWidth:36, WebkitTapHighlightColor:'transparent' }}>📦</button>
                {exportOpen && <ExportMenu gear={gear} allTags={allTags} onExport={exportItems} onClose={()=>setExportOpen(false)} isMob={true} />}
              </div>
              <button onClick={()=>setModal('info')} style={{ background:'transparent', border:'none', color:'#7a7872', fontSize:'1rem', padding:'.4rem', cursor:'pointer', minHeight:36, minWidth:36 }}>ℹ</button>
              <div style={{ position:'relative' }}>
                <button onClick={()=>setUserMenuOpen(v=>!v)} style={{ background:'transparent', border:'none', color:'#7a7872', fontSize:'1rem', padding:'.4rem', cursor:'pointer', minHeight:36, minWidth:36, WebkitTapHighlightColor:'transparent' }}>⏻</button>
                {userMenuOpen && (
                  <div style={{ position:'absolute', right:0, top:'calc(100% + 4px)', background:'#1f1f22', border:'1px solid #2e2e33', borderRadius:4, zIndex:600, minWidth:160, boxShadow:'0 8px 24px rgba(0,0,0,.7)', overflow:'hidden' }} onClick={e=>{ if(e.target===e.currentTarget) return; setUserMenuOpen(false); }}>
                    <button onClick={()=>setShowAccount(true)} style={{ display:'block', width:'100%', textAlign:'left', padding:'.65rem .9rem', background:'none', border:'none', color:'#d4d2cc', fontSize:'.68rem', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>👤 Account</button>
                    <div style={{ height:1, background:'#2e2e33' }}/>
                    <button onClick={doReverbAll} style={{ display:'block', width:'100%', textAlign:'left', padding:'.65rem .9rem', background:'none', border:'none', color:'#0eb8a0', fontSize:'.68rem', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>↺ Refresh All</button>
                    <div style={{ height:1, background:'#2e2e33' }}/>
                    <button onClick={onSignOut} style={{ display:'block', width:'100%', textAlign:'left', padding:'.65rem .9rem', background:'none', border:'none', color:'#e05252', fontSize:'.68rem', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>⏻ Logout</button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display:'flex', gap:'.5rem', alignItems:'center' }}>
              <div style={{ fontSize:'.6rem', color:'#7a7872', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.email}</div>
              <div style={{ position:'relative' }}>
                <Btn size="sm" variant="teal" onClick={doReverbAll} style={{ flexShrink:0 }}>↺ Refresh All</Btn>
                <Btn size="sm" variant="teal" onClick={()=>setExportOpen(v=>!v)}>📦 Export Vault</Btn>
                {exportOpen && <ExportMenu gear={gear} allTags={allTags} onExport={exportItems} onClose={()=>setExportOpen(false)} isMob={false} />}
              </div>
              <Btn size="sm" variant="primary" onClick={()=>{ setEditId(null); setModal('add'); }}>+ Add Gear</Btn>
              <Btn size="sm" variant="ghost" onClick={()=>setModal('info')} style={{ fontSize:'1rem', minWidth:40, minHeight:40 }}>ℹ</Btn>
              <div style={{ position:'relative' }}>
                <Btn size="sm" variant="ghost" onClick={()=>setUserMenuOpen(v=>!v)} style={{ fontSize:'1rem', minWidth:40, minHeight:40 }}>⏻</Btn>
                {userMenuOpen && (
                  <div style={{ position:'absolute', right:0, top:'calc(100% + 6px)', background:'#1f1f22', border:'1px solid #2e2e33', borderRadius:4, zIndex:600, minWidth:145, boxShadow:'0 8px 32px rgba(0,0,0,.8)', overflow:'hidden' }} onClick={()=>setUserMenuOpen(false)}>
                    <button onClick={()=>setShowAccount(true)} style={{ display:'block', width:'100%', textAlign:'left', padding:'.65rem .9rem', background:'none', border:'none', color:'#d4d2cc', fontSize:'.68rem', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>👤 Account</button>
                    <div style={{ height:1, background:'#2e2e33' }}/>
                    <button onClick={onSignOut} style={{ display:'block', width:'100%', textAlign:'left', padding:'.65rem .9rem', background:'none', border:'none', color:'#e05252', fontSize:'.68rem', fontFamily:"'Share Tech Mono',monospace", letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>⏻ Logout</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <div style={{ maxWidth:1440, margin:'0 auto', padding:isMob?'1rem':'1.5rem' }}>


        {/* ── Gear Breakdown ── */}
        {gear.length > 0 && (() => {
          const cats = Object.entries(CATS).map(([key,cat])=>({
            key, label:cat.label, color:cat.color,
            count: gear.filter(g=>g.cat===key).length,
            val:   gear.filter(g=>g.cat===key).reduce((s,g)=>s+(parseFloat(g.rv||g.mv)||0),0),
          })).filter(c=>c.count>0);

          const maxVal = Math.max(...cats.map(c=>c.val), 1);
          const total  = gear.length;
          const DS = 152, R=58, r=36, cx=76, cy=76;
          const labelW = isMob ? 68 : 100;
          const BAR_H  = 18;

          let cumA = -Math.PI/2;
          const slices = cats.map(c=>{
            const angle = (c.count/total)*Math.PI*2;
            const x1=cx+R*Math.cos(cumA), y1=cy+R*Math.sin(cumA);
            cumA += angle;
            const x2=cx+R*Math.cos(cumA), y2=cy+R*Math.sin(cumA);
            const ix1=cx+r*Math.cos(cumA-angle), iy1=cy+r*Math.sin(cumA-angle);
            const ix2=cx+r*Math.cos(cumA), iy2=cy+r*Math.sin(cumA);
            const large=angle>Math.PI?1:0;
            return { ...c, path:`M${x1.toFixed(2)},${y1.toFixed(2)} A${R},${R} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} L${ix2.toFixed(2)},${iy2.toFixed(2)} A${r},${r} 0 ${large},0 ${ix1.toFixed(2)},${iy1.toFixed(2)} Z` };
          });

          return (
            <div style={{ display:'grid', gridTemplateColumns:isMob?'1fr':`${DS+120}px 1fr`, gap:'1rem', marginBottom:'1rem', alignItems:'stretch' }}>

              {/* Donut — left, value beside on all sizes */}
              <div style={{ background:'#18181a', border:'1px solid #1e3533', borderRadius:4, padding:'.7rem', display:'flex', flexDirection:'row', alignItems:'center', justifyContent:'center', gap:'1rem' }}>
                <div style={{ flexShrink:0 }}>
                  <div style={{ fontSize:'.5rem', color:'#5a5a5a', textTransform:'uppercase', letterSpacing:'.15em', marginBottom:'.3rem' }}>Item Mix</div>
                  <GearDonutSVG cats={cats} total={total}
                    onSliceClick={key=>setFilter(f=>f===key?'All':key)}/>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:'.6rem', flex:1, minWidth:0 }}>
                  <div>
                    <div style={{ fontSize:'.45rem', color:'#5a5a5a', textTransform:'uppercase', letterSpacing:'.14em', marginBottom:'.15rem' }}>Est. Value</div>
                    <div style={{ fontFamily:"'Black Ops One',sans-serif", fontSize:'1.1rem', color:'#0eb8a0', lineHeight:1 }}>{'$'+fmt(totalVal)}</div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'.3rem' }}>
                    {slices.map(s=>(
                      <div key={s.key} style={{ display:'flex', alignItems:'center', gap:'.4rem', cursor:'pointer' }}
                        onClick={()=>setFilter(f=>f===s.key?'All':s.key)}>
                        <div style={{ width:6, height:6, borderRadius:'50%', background:s.color, flexShrink:0 }}/>
                        <div style={{ fontSize:'.5rem', color:'#7a7872', flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{s.label}</div>
                        <div style={{ fontSize:'.5rem', fontFamily:"'Black Ops One',sans-serif", color:s.color }}>{s.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bar chart — right */}
              <div style={{ background:'#18181a', border:'1px solid #1e3533', borderRadius:4, padding:'.7rem .9rem', display:'flex', flexDirection:'column' }}>
                <div style={{ fontSize:'.5rem', color:'#5a5a5a', textTransform:'uppercase', letterSpacing:'.15em', marginBottom:'.6rem' }}>Breakdown by Category</div>
                <div style={{ display:'flex', flexDirection:'column', gap:'.42rem', flex:1, justifyContent:'space-evenly' }}>
                  {cats.map(c=>(
                    <div key={c.key} style={{ display:'flex', alignItems:'center', gap:'.55rem', cursor:'pointer' }}
                      onClick={()=>setFilter(f=>f===c.key?'All':c.key)}>
                      <div style={{
                        fontSize:'.55rem', width:labelW, flexShrink:0, textAlign:'right',
                        whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                        color: filter===c.key ? c.color : '#7a7a78', transition:'color .15s',
                      }}>{c.label}</div>
                      <div style={{ flex:1, height:BAR_H, background:'#0d0d10', borderRadius:3, overflow:'hidden', position:'relative' }}>
                        <div style={{
                          position:'absolute', left:0, top:0, bottom:0,
                          width: c.val>0 ? `${(c.val/maxVal)*100}%` : '4px',
                          background:`linear-gradient(90deg, ${c.color}cc, ${c.color}88)`,
                          borderRadius:3, boxShadow:`0 0 8px ${c.color}33`,
                          transition:'width .45s ease',
                        }}/>
                        {c.val>0 && (
                          <div style={{
                            position:'absolute', left:6, top:0, bottom:0,
                            display:'flex', alignItems:'center',
                            fontSize:'.5rem', color:'#ffffffbb',
                            fontFamily:"'Share Tech Mono',monospace",
                            pointerEvents:'none', whiteSpace:'nowrap',
                          }}>${fmt(c.val)}</div>
                        )}
                      </div>
                      <div style={{
                        fontSize:'.7rem', fontFamily:"'Black Ops One',sans-serif",
                        color:c.color, width:22, textAlign:'right', flexShrink:0, lineHeight:1,
                      }}>{c.count}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          );
        })()}

        {/* ── Filter + Group-by bar ── */}
        <div style={{ display:'flex', flexDirection:'column', gap:'.5rem', marginBottom:'.75rem' }}>
          {/* Row 1: Group-by + sort + view toggles */}
          <div style={{ display:'flex', gap:'.4rem', alignItems:'center', flexWrap:'wrap' }}>
            {/* Group-by toggle */}
            <div style={{ display:'flex', border:'1px solid #2e2e33', borderRadius:4, overflow:'hidden', flexShrink:0 }}>
              {[['type','By Type'],['tag','By Label']].map(([v,lbl])=>(
                <button key={v} onClick={()=>{ const g=v; setGroupBy(g); setFilter('All'); setTagFilter(null); try{const p=JSON.parse(localStorage.getItem(PREFS_CACHE_KEY)||'{}');localStorage.setItem(PREFS_CACHE_KEY,JSON.stringify({...p,groupBy:g}));}catch{} }}
                  style={{ fontFamily:'inherit', fontSize:'.6rem', letterSpacing:'.08em', textTransform:'uppercase', padding:'.38rem .7rem', cursor:'pointer', border:'none', background:groupBy===v?T.accent:'transparent', color:groupBy===v?'#000':'#7a7872', fontWeight:groupBy===v?700:400, WebkitTapHighlightColor:'transparent', whiteSpace:'nowrap' }}>
                  {lbl}
                </button>
              ))}
            </div>
            {/* Sort dropdown */}
            <select value={sortBy} onChange={e=>{ setSortBy(e.target.value); try{const p=JSON.parse(localStorage.getItem(PREFS_CACHE_KEY)||'{}');localStorage.setItem(PREFS_CACHE_KEY,JSON.stringify({...p,sortBy:e.target.value}));}catch{} }} style={{
              fontFamily:'inherit', fontSize:'.6rem', letterSpacing:'.06em', textTransform:'uppercase',
              background:'#111114', border:'1px solid #2e2e33', color:'#7a7872',
              padding:'.38rem .55rem', borderRadius:4, cursor:'pointer', flexShrink:0,
              WebkitAppearance:'none', appearance:'none', paddingRight:'1.4rem',
              backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%237a7872'/%3E%3C/svg%3E")`,
              backgroundRepeat:'no-repeat', backgroundPosition:'right .45rem center',
            }}>
              <option value="make">Sort: Make</option>
              <option value="model">Sort: Model</option>
              <option value="value">Sort: Value ↓</option>
              <option value="year">Sort: Year ↓</option>
              <option value="added">Sort: Newest</option>
            </select>
            {/* Grid / List view toggle */}
            <div style={{ display:'flex', border:'1px solid #2e2e33', borderRadius:4, overflow:'hidden', flexShrink:0 }}>
              {[[false,'⊞'],[true,'☰']].map(([isList, icon])=>(
                <button key={icon} onClick={()=>{ setListView(isList); try{const p=JSON.parse(localStorage.getItem(PREFS_CACHE_KEY)||'{}');localStorage.setItem(PREFS_CACHE_KEY,JSON.stringify({...p,listView:isList}));}catch{} }}
                  title={isList?'List view':'Grid view'}
                  style={{ fontFamily:'inherit', fontSize:'.85rem', padding:'.28rem .55rem', cursor:'pointer', border:'none',
                    background:listView===isList?T.accent:'transparent',
                    color:listView===isList?'#000':'#7a7872',
                    WebkitTapHighlightColor:'transparent' }}>
                  {icon}
                </button>
              ))}
            </div>
          </div>
          {/* Row 2: Category / tag filter pills — own scrollable row */}
          <div style={{ display:'flex', gap:'.35rem', alignItems:'center', overflowX:'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none', paddingBottom:'.2rem' }}>
            {groupBy==='type' && ['All',...Object.keys(CATS)].map(c=>{
              const active=filter===c; const col=c==='All'?T.accent:CATS[c]?.color||'#7a7872';
              return <button key={c} onClick={()=>setFilter(c)} style={{ fontFamily:'inherit', fontSize:'.63rem', letterSpacing:'.08em', textTransform:'uppercase', padding:'.38rem .75rem', borderRadius:3, cursor:'pointer', border:`1px solid ${active?col:'#2e2e33'}`, background:active?col+'22':'transparent', color:active?col:'#7a7872', whiteSpace:'nowrap', flexShrink:0, minHeight:34, WebkitTapHighlightColor:'transparent', display:'inline-flex', alignItems:'center', gap:'.3rem' }}>{c==='All'?'All':(<><CatIcon cat={c} size={12} color={active?col:'#7a7872'}/><span>{c}</span></>)}</button>;
            })}
            {groupBy==='tag' && allTags.length>0 && ['All',...allTags].map(t=>{
              const active = t==='All'?!tagFilter:tagFilter===t;
              return <button key={t} onClick={()=>setTagFilter(t==='All'?null:t===tagFilter?null:t)} style={{ fontFamily:'inherit', fontSize:'.63rem', letterSpacing:'.06em', padding:'.38rem .75rem', borderRadius:10, cursor:'pointer', border:`1px solid ${active?T.accent2:'#2e2e33'}`, background:active?T.accent2+'22':'transparent', color:active?T.accent2:'#7a7872', whiteSpace:'nowrap', flexShrink:0, WebkitTapHighlightColor:'transparent' }}>{t==='All'?'All Labels':t}</button>;
            })}
          </div>
        </div>

        {/* ── Search ── */}
        <div style={{ position:'relative', marginBottom:'1rem' }}>
          <span style={{ position:'absolute', left:'.85rem', top:'50%', transform:'translateY(-50%)', color:'#7a7872', pointerEvents:'none', fontSize:'1rem' }}>⌕</span>
          <input style={{...inputStyle, paddingLeft:'2.4rem', fontSize:'16px'}} placeholder="Search make, model, serial…" value={search} onChange={e=>setSearch(e.target.value)} />
        </div>

        {/* ── Main content ── */}
        {loading ? (
          <div style={{ textAlign:'center', padding:'4rem', color:'#7a7872', fontSize:'.8rem', letterSpacing:'.15em' }}>
            <div style={{ fontSize:'1.8rem', marginBottom:'.75rem', opacity:.4 }}>⚡</div>
            LOADING VAULT…
          </div>
        ) : filtered.length===0 ? (
          <div style={{ textAlign:'center', padding:'4rem 2rem', border:'1px dashed #2e2e33', borderRadius:4, background:'#0d0d0e' }}>
            <PickLogo size={48} color="#2e2e33" />
            <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1.2rem', letterSpacing:'.06em', color:'#d4d2cc', marginBottom:'.5rem' }}>{gear.length===0?'Vault Is Empty':'No Results'}</div>
            <p style={{ fontSize:'.75rem', color:'#7a7872', maxWidth:260, margin:'0 auto 1.5rem', lineHeight:1.7 }}>{gear.length===0?'Add your first piece of gear below.':'Try a different search or filter.'}</p>
            {gear.length===0 && <Btn variant="primary" onClick={()=>{ setEditId(null); setModal('add'); }} style={{ minHeight:48 }}>+ Add First Gear</Btn>}
          </div>
        ) : (
          // ── Grouped sections (type or tag) ────────────────────────────────
          <div style={{ display:'flex', flexDirection:'column', gap:'2rem' }}>
            {grouped.map(({ key, label, color, items })=>(
              <div key={key}>
                {/* Section header */}
                <div style={{ display:'flex', alignItems:'center', gap:'.65rem', marginBottom:'1rem' }}>
                  <div style={{ width:40, height:40, borderRadius:6, background:`${color}18`, border:`1px solid ${color}44`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {groupBy==='type'
                      ? <CatIcon cat={key} size={22} color={color} />
                      : <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke={color} strokeWidth='1.5' strokeLinecap='round'><circle cx='5' cy='5' r='2'/><circle cx='19' cy='5' r='2'/><circle cx='5' cy='19' r='2'/><circle cx='19' cy='19' r='2'/><path d='M7 5h10M5 7v10M19 7v10M7 19h10'/></svg>
                    }
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"'Black Ops One',impact,sans-serif", fontSize:'1rem', letterSpacing:'.06em', color, lineHeight:1 }}>{label}</div>
                    <div style={{ fontSize:'.58rem', color:'#5a5a5a', letterSpacing:'.12em', marginTop:'.18rem' }}>
                      {items.length} {items.length===1?'item':'items'} · ${fmt(items.reduce((s,g)=>s+(parseFloat(g.rv||g.mv)||0),0))}
                    </div>
                  </div>
                </div>
                {listView ? (
                  <div style={{ display:'flex', flexDirection:'column', gap:'.3rem' }}>
                    {items.map(item=>(
                      <GearRow key={item.id} item={item}
                        onEdit={id=>{ setEditId(id); setModal('edit'); loadItemImgs(id); }}
                        onDelete={deleteItem}
                        onView={id=>{ setViewId(id); setModal('view'); loadItemImgs(id); }}
                        onReverb={id=>{ const g=gear.find(x=>x.id===id); if(g) doReverb(id,g); }}
                      />
                    ))}
                  </div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:isMob?'repeat(2,1fr)':'repeat(auto-fill,minmax(220px,1fr))', gap:'.75rem' }}>
                    {items.map(item=>(
                      <GearCard key={item.id} item={item}
                        onEdit={id=>{ setEditId(id); setModal('edit'); loadItemImgs(id); }}
                        onDelete={deleteItem}
                        onView={id=>{ setViewId(id); setModal('view'); loadItemImgs(id); }}
                        onReverb={id=>{ const g=gear.find(x=>x.id===id); if(g) doReverb(id,g); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Site Footer ── */}
      {!isMob && (
        <div style={{ maxWidth:1440, margin:'0 auto', padding:'0 1.5rem' }}>
          <SiteFooter onPrivacy={()=>setShowPrivacyMain(true)}/>
        </div>
      )}
      {showPrivacyMain && <PrivacyModal onClose={()=>setShowPrivacyMain(false)}/>}

      {/* ── Mobile FAB ── */}
      {isMob && (
        <div style={{ position:'fixed', bottom:0, left:0, right:0, background:'#18181a', borderTop:'1px solid #1e3533', padding:'.65rem 1rem', display:'flex', alignItems:'center', justifyContent:'center', zIndex:400, paddingBottom:'calc(env(safe-area-inset-bottom,0px) + .65rem)', boxShadow:'0 -4px 20px rgba(0,0,0,.5)' }}>
          <button onClick={()=>{ setEditId(null); setModal('add'); }}
            style={{ background:T.accent, border:'none', color:'#000', fontFamily:'inherit', fontSize:'.82rem', letterSpacing:'.1em', textTransform:'uppercase', padding:'.75rem 2.5rem', borderRadius:30, cursor:'pointer', boxShadow:`0 4px 20px ${T.accentGlow}`, minHeight:50, fontWeight:700, WebkitTapHighlightColor:'transparent' }}>
            + Add Gear
          </button>
        </div>
      )}

      {/* ── Modals ── */}
      {modal==='add' && (
        isMob
          ? <GearForm title='Add <em style="color:#0eb8a0;font-style:normal">Gear</em>' initial={{...EMPTY_FORM,_allTags:allTags}} onSave={saveGear} onCancel={()=>setModal(null)} saving={saving} onReverbLookup={doReverbFromForm} />
          : <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }} onClick={e=>{ if(e.target===e.currentTarget) setModal(null); }}><GearForm title='Add <em style="color:#0eb8a0;font-style:normal">Gear</em>' initial={{...EMPTY_FORM,_allTags:allTags}} onSave={saveGear} onCancel={()=>setModal(null)} saving={saving} onReverbLookup={doReverbFromForm} /></div>
      )}
      {modal==='edit' && editItem && (
        isMob
          ? <GearForm title='Edit <em style="color:#0eb8a0;font-style:normal">Gear</em>' initial={{...editItem,_allTags:allTags}} onSave={saveGear} onCancel={()=>{ setModal(null); setEditId(null); }} saving={saving} onReverbLookup={doReverbFromForm} />
          : <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.88)', backdropFilter:'blur(6px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }} onClick={e=>{ if(e.target===e.currentTarget){ setModal(null); setEditId(null); } }}><GearForm title='Edit <em style="color:#0eb8a0;font-style:normal">Gear</em>' initial={{...editItem,_allTags:allTags}} onSave={saveGear} onCancel={()=>{ setModal(null); setEditId(null); }} saving={saving} onReverbLookup={doReverbFromForm} /></div>
      )}
      {modal==='info' && <InfoModal onClose={()=>setModal(null)} />}
      {showAccount && <AccountModal user={user} onClose={()=>setShowAccount(false)} allTags={allTags} onFilterByTag={tags=>{ if(tags&&tags.length>0){ setGroupBy('tag'); setTagFilter(tags[0]); } }} />}
      {modal==='view' && <ViewModal item={viewItem} onClose={()=>{ setModal(null); setViewId(null); }} onEdit={id=>{ setModal('edit'); setEditId(id); setViewId(null); }} onDelete={id=>{ deleteItem(id); setModal(null); setViewId(null); }} onReverb={id=>{ const g=gear.find(x=>x.id===id); if(g) doReverb(id,g); }} />}

      {/* ── Toasts ── */}
      <div style={{ position:'fixed', bottom:isMob?'90px':'1.2rem', left:isMob?'1rem':'auto', right:'1rem', zIndex:9500, display:'flex', flexDirection:'column', gap:'.45rem', pointerEvents:'none', maxWidth:isMob?'calc(100vw - 2rem)':'295px' }}>
        {toasts.map(t=>(
          <div key={t.id} style={{ background:'#1f1f22', border:'1px solid #2e2e33', borderLeft:`3px solid ${t.type==='ok'?T.ok:t.type==='err'?T.danger:t.type==='inf'?T.info:T.accent}`, padding:'.75rem 1rem', borderRadius:3, fontSize:'.75rem', boxShadow:'0 4px 20px rgba(0,0,0,.6)', lineHeight:1.45 }}>{t.msg}</div>
        ))}
      </div>
    </div>
  );
}
